import { catalogueDictionary } from '@musicville/shared';
import type { Dict } from '../i18n';

export const en: Dict = {
  ...catalogueDictionary('en'),
  'nav.lessons': 'Lessons',
  'nav.teachers': 'Teachers',
  'nav.rooms': 'Practice Rooms',
  'nav.community': 'Community',
  'nav.about': 'About',
  'nav.contact': 'Contact',
  'nav.logIn': 'Log in',
  'nav.bookTrial': 'Book a trial lesson',
  'nav.menu': 'Menu',
  'nav.close': 'Close',

  'hero.headline': 'Find the teacher who makes it click.',
  'hero.nextTrial': 'Next trial available',
  'hero.noTrial': 'Ask us for a time',
  'picker.prompt': 'What do you want to play?',
  'picker.listen': 'Hear it',
  'picker.mute': 'Sound off',
  'picker.hint': 'Hover or tap to play',
  'picker.withTeacher': 'with {teacher}',
  'picker.bookSlot': 'Book this time',
  'picker.otherTimes': 'Other times',

  'finder.title': 'Find a lesson this week',
  'finder.sub': 'Pick a subject and a day — we will show every free 30-minute slot.',
  'finder.fullTimetable': 'See the full timetable',
  'finder.subject': 'Subject',
  'finder.day': 'Day',
  'finder.teacher': 'Teacher',
  'finder.anyDay': 'Any day this week',
  'finder.anyTeacher': 'Any available teacher',
  'finder.anySubject': 'Any subject',
  'finder.submit': 'Find times',

  'subjects.overline': 'WHAT YOU CAN LEARN',
  'subjects.title': 'Every discipline. One teacher who knows yours.',
  'subjects.title.one': '{count} discipline. One teacher who knows yours.',
  'subjects.title.other': '{count} disciplines. One teacher who knows yours.',
  'subjects.all': 'All lessons',
  'subjects.levels': 'Beginner to advanced',

  'how.overline': 'HOW IT WORKS',
  'how.title': 'Three steps to your first lesson.',
  'how.1.title': 'Choose your subject',
  'how.1.body':
    'Piano, vocal, guitar, electric guitar, drums, handpan or violin. Not sure yet? Book any of them — a trial is the point.',
  'how.2.title': 'Pick a teacher and a time',
  'how.2.body':
    'See real availability, 30 minutes at a time. Choose a specific teacher, or let us give you whoever is free first.',
  'how.3.title': 'Come in for your trial',
  'how.3.body':
    'Thirty minutes, no commitment. The details arrive by email straight away, with a reminder the day before.',

  'teachers.overline': 'THE TEACHERS',
  'teachers.title': 'The people you will actually sit next to.',
  'teachers.viewAll': 'View all teachers',
  'teachers.viewProfile': 'View profile',
  'teachers.nextAvailable': 'Next available',
  'teachers.noAvailability': 'Ask us for a time',
  'teachers.filters': 'Filters',
  'teachers.clear': 'Clear all',
  'teachers.results': 'teachers',
  'teachers.result': 'teacher',
  'teachers.empty': 'No teacher matches those filters yet.',
  'teachers.emptyHint': 'Try a different subject, or clear the filters to see everyone.',
  'teachers.level': 'Level',
  'teachers.language': 'Language',
  'teachers.sort': 'Sort by',
  'teachers.sort.availability': 'Soonest available',
  'teachers.sort.experience': 'Most experienced',
  'teachers.sort.name': 'Name',
  'teachers.about': 'About',
  'teachers.approach': 'How they teach',
  'teachers.credentials': 'Background',
  'teachers.languages': 'Teaches in',
  'teachers.availability': 'Availability',
  'teachers.related': 'Other teachers',
  'teachers.years': 'years teaching',

  'rooms.overline': 'PRACTICE ROOMS',
  'rooms.title': 'Not a student yet? You can still book the room.',
  'rooms.sub':
    'Rent a room by the hour and practise on your own — a piano room, a drum room, or the full band rehearsal space. No lesson required.',
  'rooms.seeAll': 'See rooms & availability',
  'rooms.check': 'Check availability',
  'rooms.book': 'Book this room',
  'rooms.perHour': '/ hour',
  'rooms.upTo': 'Up to',
  'rooms.people': 'people',
  'rooms.person': 'person',
  'rooms.houseRules': 'House rules',
  'rooms.thisWeek': 'This week',
  'rooms.open': 'Open',
  'rooms.hours': 'hours',
  'rooms.hour': 'hour',
  'rooms.selected': 'selected',
  'rooms.total': 'Total',
  'rooms.pickHours': 'Pick the hours you want. They need to run back to back.',
  'rooms.noneToday': 'Nothing free on this day.',

  'community.overline': 'COMMUNITY',
  'community.title': 'It does not stop at the lesson.',
  'stories.overline': 'STUDENT STORIES',
  'stories.title': 'What it actually feels like.',

  'faq.overline': 'QUESTIONS',
  'faq.title': 'Everything people ask before booking.',
  'contact.overline': 'COME AND SEE IT',
  'contact.title': 'The academy is easier to understand in person.',
  'contact.form.name': 'Full name',
  'contact.form.email': 'Email',
  'contact.form.phone': 'Phone',
  'contact.form.topic': 'What is it about?',
  'contact.form.message': 'Message',
  'contact.form.submit': 'Send message',
  'contact.form.sent': 'Thank you — we will come back to you today or tomorrow.',
  'contact.topic.lessons': 'Lessons',
  'contact.topic.practice-rooms': 'Practice rooms',
  'contact.topic.careers': 'Teaching with you',
  'contact.topic.other': 'Something else',

  'cta.title': 'Thirty minutes is all it takes to know.',
  'cta.sub': 'Book a trial lesson in any subject. No account needed, no commitment afterwards.',

  'booking.step': 'Step',
  'booking.of': 'of',
  'booking.step1': 'Subject',
  'booking.step2': 'Teacher',
  'booking.step3': 'Time',
  'booking.step4': 'Details',
  'booking.step5': 'Confirm',
  'booking.back': 'Back',
  'booking.continue': 'Continue',
  'booking.confirm': 'Confirm booking',
  'booking.exit': 'Save & exit',
  'booking.step1.title': 'What would you like to learn?',
  'booking.step1.sub':
    'Pick one to start with. You can add another instrument later — plenty of students do.',
  'booking.step2.title': 'Who would you like to learn {subjectLower} with?',
  'booking.step2.sub':
    'Every teacher below teaches {subjectLower}. If you have no preference, let us pick whoever is free soonest.',
  'booking.step2.any': 'Any available teacher',
  'booking.step2.anyHint': 'We will give you the earliest free slot.',
  'booking.step3.title': 'When suits you?',
  'booking.step3.sub': 'Trials are 30 minutes. All times are Baku time.',
  'booking.step4.title': 'How do we reach you?',
  'booking.step4.sub':
    'You do not need an account to book. We only use this to confirm the lesson and remind you on the day.',
  'booking.step5.title': 'Check everything is right.',
  'booking.step5.sub':
    'Nothing is booked until you confirm. You can change any of this now, or move the lesson later from the link we send you.',
  'booking.notes': 'Anything your teacher should know?',
  'booking.notesHint': 'Have you played before, what you would like to work on — anything at all.',
  'booking.experience': 'Have you played before?',
  'booking.whatsapp': 'Confirm and remind me on WhatsApp',
  'booking.success.title': 'Your trial lesson is booked.',
  'booking.success.sub':
    'The details are on their way to {email}, with a reminder the day before. If you ticked WhatsApp, the academy will message you there too.',
  'booking.success.reference': 'Booking reference',
  'booking.success.addCalendar': 'Add to calendar',
  'booking.success.home': 'Back to homepage',
  'booking.conflict.title': 'That time has just been taken.',
  'booking.conflict.sub':
    'Someone booked it while you were filling in your details. Everything else is still here — just pick another time.',
  'booking.noAvailability.title': 'Nothing available on this day.',
  'booking.noAvailability.sub':
    'Try another date, or choose “Any available teacher” to see more times.',
  'booking.nextWeek': 'Next week',
  'booking.prevWeek': 'Previous week',
  'booking.thisWeek': 'This week',
  'booking.failed.title': 'We could not confirm your booking.',
  'booking.tryAgain': 'Try again',
  'booking.free': 'Free',
  'booking.trial': 'Trial lesson',
  'booking.30min': '30 minutes',

  'auth.logIn.title': 'Welcome back.',
  'auth.logIn.sub': 'Log in to see your lessons, payments and schedule.',
  'auth.signUp.title': 'Create your account.',
  'auth.signUp.sub': 'Keep your lessons, payments and practice room bookings in one place.',
  'auth.forgot.title': 'Reset your password.',
  'auth.forgot.sub': 'Tell us the email on your account and we will send you a link.',
  'auth.reset.title': 'Set a new password.',
  'auth.reset.sub': 'Choose something you will remember — you will not need it often.',
  'auth.verify.title': 'Check your email.',
  'auth.verify.sub':
    'We sent a six-digit code to {email}. Enter it below to finish setting up your account.',
  'auth.email': 'Email',
  'auth.password': 'Password',
  'auth.confirmPassword': 'Confirm password',
  'auth.fullName': 'Full name',
  'auth.phone': 'Mobile number',
  'auth.rememberMe': 'Keep me logged in',
  'auth.forgotLink': 'Forgot your password?',
  'auth.logIn.submit': 'Log in',
  'auth.signUp.submit': 'Create account',
  'auth.forgot.submit': 'Send reset link',
  'auth.reset.submit': 'Save new password',
  'auth.verify.submit': 'Verify and continue',
  'auth.verify.resend': 'Send it again',
  'auth.verify.code': 'Six-digit code',
  'auth.noAccount': 'No account yet?',
  'auth.haveAccount': 'Already have an account?',
  'auth.terms': 'I accept the {policy} and {privacy}',
  'auth.terms.policy': 'booking policy',
  'auth.terms.privacy': 'privacy notice',
  'auth.forgot.sent': 'If that address is registered, a reset link is on its way.',
  'auth.backToLogin': 'Back to log in',
  'auth.pending.title': 'Almost there.',
  'auth.pending.sub':
    'We have your details for {email}. The academy office confirms new accounts by hand.',
  'auth.pending.alert.title': 'Waiting for the academy',
  'auth.pending.alert.body':
    'Somebody from the office checks new accounts on weekdays, usually the same day. You will get an email the moment yours is open — nothing else is needed from you.',
  'auth.pending.recheck': 'Check again',
  'auth.pending.contact': 'Get in touch',
  'auth.pending.wrongAccount': 'Signed in as somebody else?',
  'auth.rejected.title': 'We could not open your account.',
  'auth.rejected.sub': 'The academy has not been able to confirm a place for {email}.',
  'auth.rejected.alert.title': 'Not confirmed',
  'auth.rejected.alert.body':
    'This usually means we could not match you to a student on our books. If you think that is a mistake — or you have not started lessons with us yet — get in touch and we will sort it out.',
  'auth.brand.quote': 'The first month is the whole thing. After that you are just enjoying it.',

  'about.overline': 'ABOUT THE ACADEMY',
  'about.headline': 'A room, an instrument, and someone who has done this before.',
  'about.sub':
    'MusicVille is a music academy in Baku. We teach music one to one, we keep the rooms open for practice, and we try very hard not to make learning music feel like school.',
  'about.sub.one':
    'MusicVille is a music academy in Baku. We teach {count} instrument one to one, we keep the rooms open for practice, and we try very hard not to make learning music feel like school.',
  'about.sub.other':
    'MusicVille is a music academy in Baku. We teach {count} instruments one to one, we keep the rooms open for practice, and we try very hard not to make learning music feel like school.',
  'about.story.overline': 'WHY WE EXIST',
  'about.story.title': 'The first month',
  'about.story.p1':
    'Most people who quit an instrument quit in the first month. Not because they lack talent — because nobody told them what to practise, or why the thing they were doing with their hand was making it harder.',
  'about.story.p2':
    'So the academy is built around that month. Lessons are one to one, thirty or sixty minutes, with a teacher who adjusts to how you learn rather than working through a syllabus. You leave every lesson knowing exactly what to do before the next one.',
  'about.story.p3':
    'The practice rooms exist for the same reason. Owning an instrument is a real decision and it should come after you know you want to keep going, not before. Book a room by the hour until you are sure.',
  'about.facts.instruments': 'Instruments taught',
  'about.facts.teachers': 'Teachers',
  'about.facts.rooms': 'Practice rooms',
  'about.facts.minutes': 'Minutes to find out',
  'about.teach.overline': 'HOW WE TEACH',
  'about.teach.title': 'Four things we insist on.',
  'about.teach.1.title': 'One to one, always',
  'about.teach.1.body':
    'Group classes are cheaper and we do not run them. An hour of undivided attention is the product.',
  'about.teach.2.title': 'You bring the music',
  'about.teach.2.body':
    'Technique is built around a piece you actually want to play. Nobody learns faster from repertoire chosen for them.',
  'about.teach.3.title': 'Leave knowing what to practise',
  'about.teach.3.body':
    'The last five minutes of every lesson are spent deciding exactly what happens before the next one.',
  'about.teach.4.title': 'No commitment to start',
  'about.teach.4.body':
    'A thirty-minute trial, no contract, no pressure. If it is not right, we will say so and suggest someone else.',
  'about.rooms.overline': 'THE BUILDING',
  'about.rooms.title': 'Three studios and three practice rooms, on Nizami küçəsi.',
  'about.rooms.1': 'Studio 1 — piano & vocal',
  'about.rooms.2': 'Studio 3 — piano',
  'about.rooms.3': 'Basement — drums & band',
  'about.cta.title': 'Come and see it. Bring nothing.',
  'about.cta.contact': 'Contact the academy',

  'subject.hero.title': '{subject} lessons',
  'subject.hero.metaTrial': '{minutes}-minute trial',
  'subject.hero.metaTeachers': '{count} {subjectLower} teachers',
  'subject.hero.metaStudios': 'Studios 1–3',
  'subject.hero.book': 'Book your {subjectLower} trial',
  'subject.hero.seeTeachers': 'See {subjectLower} teachers',

  'subject.learn.overline': 'WHAT YOU LEARN',
  'subject.learn.title': 'Technique you can hear, not just practise.',
  'subject.learn.1.title': 'Hands and posture',
  'subject.learn.1.body':
    'Where the weight goes, how the wrist stays free, and why that decides everything above it.',
  'subject.learn.1.vocal.title': 'Breath and posture',
  'subject.learn.1.vocal.body':
    'Where the air comes from, how the throat stays free, and why that decides everything above it.',
  'subject.learn.2.title': 'Reading music',
  'subject.learn.2.body':
    'Notation at the speed you actually play. Sight-reading comes from doing it badly, often, on purpose.',
  'subject.learn.3.title': 'Repertoire you chose',
  'subject.learn.3.body':
    'You bring a piece. We build the technique the piece needs, rather than technique in the abstract.',
  'subject.learn.4.title': 'Theory, in context',
  'subject.learn.4.body':
    'Why the chord wants to move. Enough theory to understand what your hands are already doing.',
  'subject.learn.5.title': 'Rhythm and timing',
  'subject.learn.5.body':
    'Metronome work that is not punishment — subdivision, rubato, and knowing which one you need.',
  'subject.learn.6.title': 'Playing for people',
  'subject.learn.6.body':
    'Optional. Termly student evenings for anyone who wants the practice of being nervous.',

  'subject.format.overline': 'LESSON FORMAT',
  'subject.format.title': 'How {subjectLower} lessons run.',
  'subject.format.trial.title': 'The trial',
  'subject.format.trial.badge': '{minutes} minutes',
  'subject.format.trial.body':
    'A real lesson, not an interview. You play something, the teacher shows you one thing that changes it, and you decide whether to come back.',
  'subject.format.regular.title': 'Regular lessons',
  'subject.format.regular.badge': '60 minutes, weekly',
  'subject.format.regular.body':
    'Warm-up and technique, work on the current piece, then five minutes deciding exactly what to practise before next week.',
  'subject.format.bring.title': 'What to bring',
  'subject.format.bring.badge': 'Nothing',
  'subject.format.bring.body':
    'The studios have the instrument. Bring music if you already have some, and a way to make notes.',

  'subject.teachers.overline': 'WHO TEACHES IT',
  'subject.teachers.title': '{count} teachers. Pick by availability or by feel.',
  'subject.teachers.all': 'All {subjectLower} teachers',

  'subject.avail.overline': 'BOOK A TRIAL',
  'subject.avail.title': 'Free {minutes}-minute {subjectLower} slots this week',
  'subject.avail.sub':
    'Across every {subjectLower} teacher. Pick a time and we will match you with whoever is free — or choose a specific teacher first.',

  'subject.faq.overline': '{subject} questions',
  'subject.faq.title': 'Before your first {subjectLower} lesson.',

  'subject.cta.overline': 'READY WHEN YOU ARE',
  'subject.cta.title': 'Thirty minutes of {subjectLower} tells you more than this page can.',
  'subject.prices.title': 'Prices',
  'subject.prices.individual': 'One to one',
  'subject.prices.monthly': '8 lessons a month',
  'subject.prices.monthlyHint': 'Twice a week',
  'subject.prices.onceWeekly': '4 lessons a month',
  'subject.prices.onceWeeklyHint': 'Once a week',
  'subject.prices.threeMonth': '3-month package',
  'subject.prices.threeMonthHint': 'Paid for three months at once',
  'subject.prices.perMonth': '{amount} a month',
  'subject.prices.single': 'Single lesson',
  'subject.prices.group': 'Group lessons',
  'subject.prices.groupLessons.one': '{count} lesson',
  'subject.prices.groupLessons.other': '{count} lessons',
  'subject.prices.discount': 'Discount',
  'subject.prices.was': 'Regular price',
  'subject.prices.note': 'Prices in manat. You pay at the academy.',

  'roomsPage.overline': 'PRACTICE ROOMS',
  'roomsPage.title': 'A room of your own, by the hour.',
  'roomsPage.sub':
    'Three rooms, open to students and to anyone else. Book an hour, bring what you are working on, and play at whatever volume the room allows. No lesson required.',
  'roomsPage.duration': 'Duration',
  'roomsPage.dur.60': '1 hour',
  'roomsPage.dur.90': '1.5 hours',
  'roomsPage.dur.120': '2 hours',
  'roomsPage.dur.180': '3 hours',
  'roomsPage.count': '{date} · {count} rooms',
  'roomsPage.freeToday': '{count} hours free',
  'roomsPage.noneFree': 'Nothing free',
  'roomsPage.taken': 'Taken',
  'roomsPage.sampleRate': 'Sample rate',
  'roomsPage.seeRoom': 'See the room',
  'roomsPage.privacy':
    'Hours already taken show as “Taken”. We never show who booked a room, or what they booked it for.',
  'roomsPage.rules.overline': 'HOUSE RULES',
  'roomsPage.rules.title': 'Three things we ask.',
  'roomsPage.rules.1.title': 'Leave it as you found it',
  'roomsPage.rules.1.body': 'Stool back, lid down, cables coiled. The next person has an hour too.',
  'roomsPage.rules.2.title': 'Your hour is your hour',
  'roomsPage.rules.2.body': 'Arrive on time and finish on time — rooms are booked back to back.',
  'roomsPage.rules.3.title': 'Tell us if something breaks',
  'roomsPage.rules.3.body':
    'Nobody is charged for a string or a stick. We just need to know before the next booking.',

  'teachersPage.overline': 'TEACHERS',
  'teachersPage.title': '{count} teachers. One of them is yours.',
  'teachersPage.sub':
    'Every teacher here works one to one and keeps their own timetable. Filter by instrument, or by who has time this week.',
  'teachersPage.search': 'Search by name or instrument',
  'teachersPage.availableThisWeek': 'Available this week',
  'teachersPage.allSubjects': 'All subjects',

  'contactPage.overline': 'CONTACT',
  'contactPage.title': 'Ask us anything.',
  'contactPage.sub':
    'WhatsApp is fastest — someone answers during opening hours. Or use the form and we will come back to you the same day.',
  'contactPage.formTitle': 'Send us a message',
  'contactPage.name': 'Your name',
  'contactPage.phone': 'Mobile number',
  'contactPage.email': 'Email',
  'contactPage.message': 'Message',
  'contactPage.whatsappInstead': 'Message on WhatsApp instead',
  'contactPage.findUs': 'Find us',
  'contactPage.label.address': 'Address',
  'contactPage.label.whatsapp': 'WhatsApp',
  'contactPage.label.phone': 'Phone',
  'contactPage.label.email': 'Email',
  'contactPage.label.hours': 'Opening hours',
  'contactPage.fastest': 'Fastest way to reach us',
  'contactPage.fastestBody':
    'Booking questions, timetable changes, anything about a lesson — WhatsApp is answered during opening hours.',
  'contactPage.openWhatsapp': 'Open WhatsApp',

  'auth.logIn.footerLink': 'Book a trial lesson — no account needed.',
  'auth.logIn.error.title': 'That email and password do not match.',
  'auth.logIn.error.body':
    'Check them and try again, or reset your password — we will send a link to your email.',
  'auth.signUp.phoneHelper': 'The academy uses this to reach you on WhatsApp.',
  'auth.signUp.passwordHelper': 'At least 10 characters.',
  'auth.signUp.consent':
    'The academy may message me on WhatsApp about my lessons and payments. You can turn this off in your profile at any time.',
  'auth.forgot.emailHelper': 'The link is valid for 30 minutes.',
  'auth.forgot.footer': 'Cannot access that email?',
  'auth.forgot.footerLink': 'Message us on WhatsApp',
  'auth.reset.newPassword': 'New password',
  'auth.reset.confirmPassword': 'Confirm new password',
  'auth.reset.passwordHelper': 'At least 10 characters.',
  'auth.reset.footer': 'Changed your mind?',
  'auth.strength.weak': 'Weak password',
  'auth.strength.fair': 'Fair password',
  'auth.strength.good': 'Good password',
  'auth.strength.strong': 'Strong password',
  'auth.verify.digit': 'Digit {n} of 6',
  'auth.verify.resendPrompt': 'Did not get it?',
  'auth.verify.resendIn': 'Send again in {seconds}',
  'auth.verify.footer': 'Wrong number?',
  'auth.verify.footerLink': 'Change it',
  'auth.brand.attribution': 'Rəna Səfərova · Piano',

  'booking.stepOf': 'Step {n} of {total}',

  'booking.step1.note': 'Trial lessons are 30 minutes and free of commitment.',
  'booking.step1.unsure': 'Not sure yet',
  'booking.step1.unsureHint': 'Tell us at the lesson — we will help you choose',
  'booking.step4.name': 'Full name',
  'booking.step4.nameHelper': 'So the teacher knows who to expect.',
  'booking.step4.phoneHelper': 'The academy may call or message you here about the lesson.',
  'booking.step4.email': 'Email',
  'booking.step4.emailHelper': 'Your confirmation, and a reminder the day before, come here.',
  'booking.step4.notes': 'Anything we should know?',
  'booking.step4.notesHelper': 'Previous experience, goals, or anything else useful.',
  'booking.step4.consent': 'The academy may message me on WhatsApp about this booking',
  'booking.step4.consentBody':
    'Only about this lesson — to confirm it, or if something changes. You can ask them to stop at any time.',
  'booking.step4.continue': 'Review booking',
  'booking.review.overline': 'YOUR TRIAL LESSON',
  'booking.review.heading': '{subject} with {teacher}',
  'booking.review.subject': 'Subject',
  'booking.review.teacher': 'Teacher',
  'booking.review.date': 'Date',
  'booking.review.time': 'Time',
  'booking.review.duration': 'Duration',
  'booking.review.location': 'Location',
  'booking.review.name': 'Your name',
  'booking.review.mobile': 'Mobile (WhatsApp)',
  'booking.review.minutes': '{minutes} minutes',
  'booking.change': 'Change',
  'booking.preview.title': 'What we will email you',
  'booking.preview.confirmed': 'Your trial lesson is confirmed.',
  'booking.preview.manage': 'Manage or cancel: {url}',
  'booking.preview.note': 'Sent now · reminder the day before your lesson',
  'booking.termsAgree':
    'I agree to the academy’s booking terms and understand how to cancel or reschedule.',

  'booking.step2.titleAny': 'Who would you like to learn with?',
  'booking.step2.subAny':
    'Every teacher below takes beginners. If you have no preference, let us pick whoever is free soonest.',

  'booking.success.openWhatsapp': 'Open WhatsApp',
  'booking.success.accountTitle': 'Want to keep track of it?',
  'booking.success.accountBody':
    'Create an account and your lesson, schedule and payments all live in one place. It takes about twenty seconds.',
  'booking.success.accountCta': 'Create my account',
  'booking.success.accountNote':
    'Not now? Your booking is confirmed either way — everything you need is in the email.',
  'booking.failed.sub':
    'Nothing has been charged and the time is still free. Try again — or message us and we will book it for you in a minute.',
  'booking.failed.retry': 'Try again',
  'booking.failed.whatsapp': 'Message us on WhatsApp',
  'booking.failed.reference':
    'Reference: {reference} · If this keeps happening, send us this code.',

  'booking.noAvailability.cta': 'Try another date',

  'teacherPage.next': 'Next free trial slot — {when}',
  'teacherPage.nextHint': 'Trial lessons are {minutes} minutes. Pick any open time below.',
  'teacherPage.book': 'Book a trial lesson with {name}',
  'teacherPage.whatsapp': 'Message on WhatsApp',
  'teacherPage.fact.since': 'Teaching since',
  'teacherPage.fact.length': 'Lesson length',
  'teacherPage.fact.speaks': 'Speaks',
  'teacherPage.lessonLength': '{trial} / 60 min',
  'teacherPage.about.overline': 'ABOUT',
  'teacherPage.about.title': 'How I teach',
  'teacherPage.about.approach': 'Approach',
  'teacherPage.about.experience': 'Experience',
  'teacherPage.about.background': 'Background',
  'teacherPage.avail.overline': 'AVAILABILITY',
  'teacherPage.avail.title': 'Book a {minutes}-minute trial with {name}',
  'teacherPage.avail.sub':
    'Pick any open time. You will confirm the details on the next screen — nothing is booked until you do.',
  'teacherPage.selected': 'Selected: {when}',
  'teacherPage.others.overline': 'ALSO TEACHING {subject}',
  'teacherPage.others.title': 'Not sure {name} is the right fit?',
  'teacherPage.cta.title': 'One lesson tells you more than any description.',

  'roomPage.availableToday': 'Available today',
  'roomPage.fullToday': 'Fully booked today',
  'roomPage.fact.capacity': 'Capacity',
  'roomPage.fact.rate': 'Rate',
  'roomPage.fact.min': 'Min. booking',
  'roomPage.fact.open': 'Open',
  'roomPage.capacityValue': 'Up to {count}',
  'roomPage.minBooking': '1 hour',
  'roomPage.inThisRoom': 'In this room',
  'roomPage.beforeYouBook': 'Before you book',
  'roomPage.chooseDay': 'Choose a day',
  'roomPage.startTime': 'Start time',
  'roomPage.howLong': 'How long?',
  'roomPage.hoursTimes': '{hours} × {rate}',
  'roomPage.bookThis': 'Book this room',
  'roomPage.bookNote':
    'You will confirm the details on the next screen. Nothing is charged until you do.',
  'roomPage.week.overline': 'AVAILABILITY',
  'roomPage.week.title': '{room} this week',
  'roomPage.noTimes': 'No free times on this day.',

  'roomBooking.title': 'Who is the room for?',
  'roomBooking.sub':
    'You do not need an account. We use this to confirm the booking and to let you in.',
  'roomBooking.payment': 'Payment',
  'roomBooking.payCard': 'Pay now by card',
  'roomBooking.payCardHint': 'Confirms the room immediately.',
  'roomBooking.payCardUnavailable':
    'Card payment is not connected yet — book now and pay at the academy.',
  'roomBooking.payLater': 'Pay at the academy',
  'roomBooking.payLaterHint': 'We hold the room for 30 minutes before your slot.',
  'roomBooking.consent':
    'The academy may message me on WhatsApp about this booking, including if it needs to change.',
  'roomBooking.confirm': 'Confirm booking',
  'roomBooking.summary.overline': 'YOUR ROOM BOOKING',
  'roomBooking.summary.date': 'Date',
  'roomBooking.summary.time': 'Time',
  'roomBooking.summary.duration': 'Duration',
  'roomBooking.summary.rate': 'Rate',
  'roomBooking.summary.location': 'Location',
  'roomBooking.summary.reference': 'Reference',
  'roomBooking.summary.hours': '{hours} hours',
  'roomBooking.summary.hour': '{hours} hour',
  'roomBooking.done.title': '{room} is yours.',
  'roomBooking.done.sub': '{date} from {time}. The details are on their way to {email}.',
  'roomBooking.done.again': 'Book another hour',
  'roomBooking.rules.title': 'Before you come in',
  'roomBooking.rules.body':
    'Rooms run back to back, so please finish on time. Leave the stool back and the lid down, and tell us if anything breaks — nobody is charged for it.',

  'cta.overline': 'READY TO START?',

  'contact.sub':
    'Drop in, look at the rooms, meet a teacher. Or message us — someone answers on WhatsApp during opening hours.',
  'contact.mapTitle': 'Map: how to find MusicVille Academy',
  'contact.hoursShort': '{days}, {hours}',
  'contact.closedOn': '{days}: closed',
  'common.weekdayShort.0': 'Sun',
  'common.weekdayShort.1': 'Mon',
  'common.weekdayShort.2': 'Tue',
  'common.weekdayShort.3': 'Wed',
  'common.weekdayShort.4': 'Thu',
  'common.weekdayShort.5': 'Fri',
  'common.weekdayShort.6': 'Sat',
  'contact.whatsappLine': '{phone} — WhatsApp',

  'footer.learn': 'Learn',
  'footer.academy': 'Academy',
  'footer.support': 'Support',
  'footer.getInTouch': 'Get in touch',
  'footer.bookingPolicy': 'Booking policy',
  'footer.privacy': 'Privacy',
  'footer.faq': 'FAQ',
  'footer.aboutUs': 'About us',
  'footer.rights': '© 2026 MusicVille Academy',

  'common.loading': 'Loading…',
  'common.retry': 'Try again',
  'common.error': 'Something went wrong.',
  'common.errorHint': 'We could not load that. It is usually temporary.',
  'common.optional': 'optional',
  'common.required': 'required',
  'common.showMore': 'Show more',
  'common.showLess': 'Show less',
  'common.notFound': 'We could not find that page.',
  'common.notFoundHint': 'It may have moved, or the link may be out of date.',
  'common.goHome': 'Back to the homepage',
  'common.skipToContent': 'Skip to content',

  'portal.nav.label': 'Portal',
  'portal.nav.current': '(current page)',
  'portal.nav.home': 'Home',
  'portal.nav.schedule': 'My Schedule',
  'portal.nav.explore': 'Explore Lessons',
  'portal.nav.rooms': 'Practice Rooms',
  'portal.nav.community': 'Community',
  'portal.nav.payments': 'Payments',
  'portal.nav.notifications': 'Notifications',
  'portal.nav.profile': 'Profile',
  'portal.nav.logOut': 'Log out',
  'portal.role.student': 'Student',
  'portal.role.teacher': 'Teacher',
  'portal.role.admin': 'Academy office',
  'portal.theme.label': 'Appearance',
  'portal.theme.light': 'Light',
  'portal.theme.dark': 'Dark',
  'portal.theme.system': 'Auto',
  'portal.topbar.search': 'Search the community',
  'portal.topbar.notifications': 'Notifications',
  'portal.topbar.notificationsUnread.one': 'Notifications, 1 unread',
  'portal.topbar.notificationsUnread.other': 'Notifications, {count} unread',
  'portal.breadcrumbs': 'Breadcrumbs',
  'portal.dashboard.goodMorning': 'Good morning, {name}',
  'portal.dashboard.goodAfternoon': 'Good afternoon, {name}',
  'portal.dashboard.goodEvening': 'Good evening, {name}',
  'portal.dashboard.weekEmpty': 'nothing booked this week',
  'portal.dashboard.weekLessons.one': 'one lesson this week',
  'portal.dashboard.weekLessons.other': '{count} lessons this week',
  'portal.dashboard.weekRooms.one': 'one room booking this week',
  'portal.dashboard.weekRooms.other': '{count} room bookings this week',
  'portal.dashboard.weekBoth': '{lessons} lessons and {rooms} room bookings this week',
  'portal.dashboard.paymentDueTitle.one': 'Your next payment is due tomorrow.',
  'portal.dashboard.paymentDueTitle.other': 'Your next payment is due in {count} days.',
  'portal.dashboard.paymentOverdueTitle': 'Your last payment is overdue.',
  'portal.dashboard.paymentDueBody':
    '{amount} for the {period}. Pay any time before {date} — we will remind you again on the day.',
  'portal.dashboard.nextLesson': 'Your next lesson',
  'portal.dashboard.viewSchedule': 'View schedule',
  'portal.dashboard.viewDetails': 'View details',
  'portal.dashboard.manageBooking': 'Manage booking',
  'portal.dashboard.allPayments': 'All payments',
  'portal.dashboard.thisWeek': 'This week',
  'portal.dashboard.openSchedule': 'Open my schedule',
  'portal.dashboard.quickActions': 'Quick actions',
  'portal.dashboard.fromCommunity': 'From the community',
  'portal.dashboard.openCommunity': 'Open community',
  'portal.dashboard.exploreLessons': 'Explore lessons',
  'portal.dashboard.checkAvailability': 'Check availability',
  'portal.dashboard.noLessonsTitle': 'Nothing booked yet.',
  'portal.dashboard.noLessonsBody':
    'Pick a subject and a time — a trial is thirty minutes and commits you to nothing.',
  'portal.dashboard.quietWeekTitle': 'A quiet week.',
  'portal.dashboard.quietWeekBody':
    'Nothing in the diary between now and Sunday. A practice room is bookable by the hour if you want one.',
  'portal.dashboard.action.instrument.title': 'Start another instrument',
  'portal.dashboard.action.instrument.body':
    'Book a trial in a second subject — most students add one within a year.',
  'portal.dashboard.action.room.title': 'Book a practice room',
  'portal.dashboard.action.room.body': 'Rent a room by the hour and practise on your own.',
  'portal.dashboard.action.community.title': 'Ask the community',
  'portal.dashboard.action.community.body':
    'Get an answer from another student, or from a teacher.',
  'portal.schedule.filterLabel': 'Which bookings to show',
  'portal.schedule.all': 'All',
  'portal.schedule.lessons': 'Lessons',
  'portal.schedule.rooms': 'Practice rooms',
  'portal.schedule.today': 'Today',
  'portal.schedule.previousWeek': 'Previous week',
  'portal.schedule.nextWeek': 'Next week',
  'portal.schedule.week': 'Week {number}',
  'portal.schedule.weekCaption': 'Your bookings, by day of the week',
  'portal.schedule.nothingBooked': 'Nothing booked',
  'portal.schedule.upcoming': 'Upcoming',
  'portal.schedule.past': 'Past lessons',
  'portal.schedule.seeAll': 'See all',
  'portal.schedule.noUpcomingTitle': 'Nothing coming up.',
  'portal.schedule.noUpcomingBody':
    'Your diary is clear. Book a lesson or a practice room whenever you are ready.',
  'portal.schedule.lesson': 'Lesson',
  'portal.schedule.aTeacher': 'a teacher',
  'portal.schedule.lessonWith': '{subject} with {teacher}',
  'portal.schedule.trialWith': '{subject} trial with {teacher}',
  'portal.schedule.trialShort': '{subject} trial',
  'portal.schedule.minutes.one': '1 minute',
  'portal.schedule.minutes.other': '{count} minutes',
  'portal.schedule.hours.one': '1 hour',
  'portal.schedule.hours.other': '{count} hours',
  'portal.schedule.cancelledByAcademy': 'Cancelled by the academy',
  'portal.schedule.cancelledByYou': 'You cancelled this booking',
  'portal.schedule.notFound': 'We could not find that booking.',
  'portal.schedule.duration': 'Duration',
  'portal.schedule.room': 'Room',
  'portal.schedule.type': 'Lesson type',
  'portal.schedule.typeRegular': 'Regular lesson',
  'portal.schedule.typeTrial': 'Trial lesson',
  'portal.schedule.typeRoom': 'Practice room',
  'portal.schedule.bookedOn': 'Booked on',
  'portal.schedule.thisPeriod': 'This period',
  'portal.schedule.attendedOf': '{attended} of {scheduled} lessons attended',
  'portal.schedule.teacherSince': '{subject} · your teacher since {when}',
  'portal.schedule.addToCalendar': 'Add to calendar',
  'portal.schedule.askToReschedule': 'Ask to reschedule',
  'portal.schedule.cancelLesson': 'Cancel lesson',
  'portal.schedule.cancellationTerms':
    'Cancellation terms are set by the academy — you will see them before anything is confirmed.',
  'portal.schedule.tooLateToChange':
    'This is too close to change from here. Message the academy on WhatsApp and they will sort it out.',
  'portal.schedule.calendarSummary': 'MusicVille Academy lesson',
  'portal.badge.lesson': 'Lesson',
  'portal.badge.trial': 'Trial',
  'portal.badge.room': 'Room',
  'portal.badge.cancelled': 'Cancelled',
  'portal.badge.cancelledByAcademy': 'Cancelled by academy',
  'portal.badge.attended': 'Attended',
  'portal.badge.absent': 'Absent',
  'portal.badge.pending': 'Holding',
  'portal.badge.upcoming': 'Upcoming',
  'portal.badge.scheduled': 'Scheduled',
  'portal.badge.paid': 'Paid',
  'portal.badge.refunded': 'Refunded',
  'portal.badge.overdue': 'Overdue',
  'portal.badge.due': 'Due',
  'portal.badge.dueToday': 'Due today',
  'portal.badge.dueInDays.one': 'Due tomorrow',
  'portal.badge.dueInDays.other': 'Due in {count} days',
  'portal.explore.title': 'Add a second instrument.',
  'portal.explore.lead':
    'The second instrument is always easier than the first — start with a 30-minute trial, same as before.',
  'portal.explore.prefill':
    'Your name and number are already on file — booking a second subject takes two taps.',
  'portal.explore.teachersWithTime': 'Teachers with time this week',
  'portal.explore.allTeachers': 'All teachers',
  'portal.explore.nextAvailable': 'Next available: {when}',
  'portal.rooms.title': 'Book a room to practise.',
  'portal.rooms.lead':
    'Pick a day, then pick an hour. Rooms are yours for as long as you book them — no lesson required.',
  'portal.rooms.perHour': '{price} / hour',
  'portal.rooms.sampleRate': 'Sample rate',
  'portal.rooms.book': 'Book this room',
  'portal.rooms.taken': 'Taken',
  'portal.rooms.upTo.one': 'up to 1 person',
  'portal.rooms.upTo.other': 'up to {count} people',
  'portal.rooms.closed': 'Closed on this day.',
  'portal.rooms.privacy': 'Hours already taken show as “Taken”. We never show who booked a room.',
  'portal.payments.lead':
    'Everything you owe, everything you have paid, and exactly how it was worked out.',
  'portal.payments.periodLabel': '{month} period',
  'portal.payments.payNow': 'Pay now',
  'portal.payments.payAmount': 'Pay {amount} now',
  'portal.payments.viewReceipt': 'View receipt',
  'portal.payments.downloadReceipt': 'Download receipt',
  'portal.payments.dueIn.one': 'Due tomorrow',
  'portal.payments.dueIn.other': 'Due in {count} days',
  'portal.payments.dueToday': 'Due today',
  'portal.payments.overdueSince': 'Overdue since {when}',
  'portal.payments.paidOn': 'Paid {when}',
  'portal.payments.lessonsScheduled.one': '1 lesson scheduled',
  'portal.payments.lessonsScheduled.other': '{count} lessons scheduled',
  'portal.payments.attended.one': '1 attended',
  'portal.payments.attended.other': '{count} attended',
  'portal.payments.cancelled.one': '1 cancelled',
  'portal.payments.cancelled.other': '{count} cancelled',
  'portal.payments.upcoming.one': '1 upcoming',
  'portal.payments.upcoming.other': '{count} upcoming',
  'portal.payments.howCalculated': 'How this period was calculated',
  'portal.payments.scheduledLessons': 'Scheduled lessons',
  'portal.payments.rate': 'Rate',
  'portal.payments.attendedSoFar': 'Attended so far',
  'portal.payments.cancelledByAcademy': 'Cancelled by the academy',
  'portal.payments.stillUpcoming': 'Still upcoming',
  'portal.payments.adjustments': 'Adjustments',
  'portal.payments.totalFor': 'Total for {period}',
  'portal.payments.policyNote':
    'The interface supports monthly packages, fixed lesson packages and per-lesson billing — the academy’s policy decides which applies and whether cancellations adjust the total.',
  'portal.payments.history': 'Payment history',
  'portal.payments.historyCaption': 'Your invoices, newest first',
  'portal.payments.noHistory': 'No payments yet.',
  'portal.payments.col.date': 'Date',
  'portal.payments.col.period': 'Period',
  'portal.payments.col.description': 'Description',
  'portal.payments.col.amount': 'Amount',
  'portal.payments.col.status': 'Status',
  'portal.payments.col.receipt': 'Receipt',
  'portal.payments.openInvoice': 'Open invoice {invoice}',
  'portal.payments.nothingDueTitle': 'Nothing to pay.',
  'portal.payments.nothingDueBody':
    'You are up to date. The next period will appear here when it is raised.',
  'portal.payments.notFound': 'We could not find that invoice.',
  'portal.payments.invoice': 'invoice {invoice}',
  'portal.payments.lessonsInPeriod': 'Lessons in this period',
  'portal.payments.noLines': 'No lessons were billed on this invoice.',
  'portal.payments.breakdown': 'Breakdown',
  'portal.payments.lessonsScheduledLabel': 'Lessons scheduled',
  'portal.payments.attendedLabel': 'Attended',
  'portal.payments.upcomingLabel': 'Upcoming',
  'portal.payments.amountDue': 'Amount due',
  'portal.payments.amountPaid': 'Amount paid',
  'portal.payments.amountRefunded': 'Amount refunded',
  'portal.community.title': 'MusicVille Community',
  'portal.community.lead':
    'Ask anything about practice, technique or your instrument. Students answer. Teachers answer too.',
  'portal.community.ask': 'Ask a question',
  'portal.community.searchLabel': 'Search the community',
  'portal.community.searchPlaceholder': 'Search questions and answers',
  'portal.community.allTopics': 'All',
  'portal.community.teacher': 'Teacher',
  'portal.community.answers.one': '1 answer',
  'portal.community.answers.other': '{count} answers',
  'portal.community.foundUseful.one': '1 found this useful',
  'portal.community.foundUseful.other': '{count} found this useful',
  'portal.community.save': 'Save',
  'portal.community.saved': 'Saved',
  'portal.community.popular': 'Popular this week',
  'portal.community.howItWorks': 'How this works',
  'portal.community.guidelines':
    'This is a private space for MusicVille students and teachers. Ask real questions, answer generously, and keep it about the music. Teacher answers are marked with a badge.',
  'portal.community.noResultsTitle': 'Nothing matches that.',
  'portal.community.noResultsBody':
    'Try a different topic, or ask it yourself — somebody here has almost certainly wondered the same thing.',
  'portal.community.notFound': 'We could not find that question.',
  'portal.community.asked': '{context} · asked {when}',
  'portal.community.markedHelpful': 'Marked helpful',
  'portal.community.related': 'Related questions',
  'portal.community.noAnswers': 'No answers yet. Be the first.',
  'portal.community.yourAnswer': 'Your answer',
  'portal.community.answerPlaceholder': 'Share what actually worked for you…',
  'portal.community.answerHint':
    'Be specific — “practise slowly” helps less than “play it at 60bpm until it is even”.',
  'portal.community.postAnswer': 'Post answer',
  'portal.community.askTitle': 'Ask the community',
  'portal.community.askLead':
    'Someone here has almost certainly had the same question. Teachers read this too.',
  'portal.community.question': 'Question',
  'portal.community.questionHelper': 'Ask it the way you would say it out loud.',
  'portal.community.moreDetail': 'More detail',
  'portal.community.moreDetailHelper': 'Optional, but questions with detail get better answers.',
  'portal.community.moreDetailPlaceholder':
    'What have you tried, what is confusing, and what would a good answer look like?',
  'portal.community.topic': 'Topic',
  'portal.community.instrument': 'Instrument',
  'portal.community.chooseSubject': 'Choose a subject',
  'portal.community.cancel': 'Cancel',
  'portal.community.postQuestion': 'Post question',
  'portal.community.goodQuestion': 'A good question',
  'portal.community.tip1': 'Says what you have already tried',
  'portal.community.tip2': 'Names the instrument and your level',
  'portal.community.tip3': 'Asks one thing, not five',
  'portal.community.tip4': 'Avoids anything personal about other students',
  'portal.topic.technique': 'Technique',
  'portal.topic.theory': 'Music Theory',
  'portal.topic.practice': 'Practice',
  'portal.topic.instruments': 'Instruments',
  'portal.topic.performance': 'Performance',
  'portal.topic.academy': 'Academy',
  'portal.notifications.lead': 'Everything about your lessons, rooms and payments.',
  'portal.notifications.markAllRead': 'Mark all as read',
  'portal.notifications.all': 'All',
  'portal.notifications.unread': 'Unread',
  'portal.notifications.today': 'Today',
  'portal.notifications.thisWeek': 'This week',
  'portal.notifications.earlier': 'Earlier',
  'portal.notifications.emptyTitle': 'Nothing here yet.',
  'portal.notifications.emptyBody':
    'Reminders about your lessons, rooms and payments will show up here.',
  'portal.notifications.category.lesson': 'Lessons',
  'portal.notifications.category.schedule': 'Schedule changes',
  'portal.notifications.category.room': 'Room bookings',
  'portal.notifications.category.payment': 'Payments',
  'portal.notifications.category.community': 'Community',
  'portal.profile.title': 'Profile & settings',
  'portal.profile.personalDetails': 'Personal details',
  'portal.profile.photo': 'Profile photo',
  'portal.profile.photoHelper':
    'Optional. Your teacher sees this next to your name in their schedule.',
  'portal.profile.uploadPhoto': 'Upload photo',
  'portal.profile.removePhoto': 'Remove',
  'portal.profile.mobile': 'Mobile number (WhatsApp)',
  'portal.profile.mobileHelper': 'This is where confirmations and reminders go.',
  'portal.profile.emailHelper': 'Message the academy if you need this changed.',
  'portal.profile.language': 'Language',
  'portal.profile.languageHelper': 'Also the language of your emails and notifications.',
  'portal.profile.saveChanges': 'Save changes',
  'portal.profile.saved': 'Saved.',
  'portal.profile.notificationsLead':
    'The academy decides when reminders are sent. You decide where they go.',
  'portal.profile.prefFailed': 'We could not save that. Try the switch again.',
  'portal.profile.pref.lessonReminders': 'Lesson reminders by email',
  'portal.profile.pref.lessonReminders.body': 'An email the day before each lesson.',
  'portal.profile.pref.bookingConfirmations': 'Booking confirmations by email',
  'portal.profile.pref.bookingConfirmations.body': 'Sent the moment you book.',
  'portal.profile.pref.paymentReminders': 'Payment reminders',
  'portal.profile.pref.paymentReminders.body': 'Before a payment is due, and if one is overdue.',
  'portal.profile.pref.scheduleChanges': 'Schedule changes',
  'portal.profile.pref.scheduleChanges.body': 'If a teacher moves or cancels a lesson.',
  'portal.profile.pref.communityReplies': 'Community replies',
  'portal.profile.pref.communityReplies.body': 'When someone answers your question.',
  'portal.profile.pref.academyNews': 'Academy news by email',
  'portal.profile.pref.academyNews.body': 'Occasional — concerts, holidays, new teachers.',
  'portal.profile.yourSubjects': 'Your subjects',
  'portal.profile.teacherSince': '{teacher} · since {when}',
  'portal.profile.trialBookedWith': 'Trial booked with {teacher}',
  'portal.profile.noSubjects': 'No subjects yet. Book a trial and this fills itself in.',
  'portal.profile.addSubject': 'Add another subject',
  'portal.profile.security': 'Security',
  'portal.profile.lastChanged': 'Last changed {when}',
  'portal.profile.neverChanged': 'Never changed.',
  'portal.profile.changePassword': 'Change password',
  'portal.profile.currentPassword': 'Current password',
  'portal.profile.passwordWrong': 'That is not your current password.',
  'portal.profile.otherSessions': 'Changing your password signs you out everywhere else.',

  'common.save': 'Save',
  'common.dismiss': 'Dismiss',

  'teacher.nav.students': 'Students',
  'teacher.nav.attendance': 'Attendance',

  'teacher.level.beginner': 'Beginner',
  'teacher.level.intermediate': 'Intermediate',
  'teacher.level.advanced': 'Advanced',

  'teacher.weekday.1': 'Monday',
  'teacher.weekday.2': 'Tuesday',
  'teacher.weekday.3': 'Wednesday',
  'teacher.weekday.4': 'Thursday',
  'teacher.weekday.5': 'Friday',
  'teacher.weekday.6': 'Saturday',
  'teacher.weekday.0': 'Sunday',

  'teacher.lesson.lesson': 'Lesson',
  'teacher.lesson.trial': 'Trial',
  'teacher.lesson.newStudent': 'New student',
  'teacher.lesson.minutes.one': '1 min',
  'teacher.lesson.minutes.other': '{count} min',
  'teacher.lesson.date': 'Date',
  'teacher.lesson.time': 'Time',
  'teacher.lesson.duration': 'Duration',
  'teacher.lesson.room': 'Room',
  'teacher.lesson.roomTbc': 'Not assigned yet',
  'teacher.lesson.studentSince': 'your student since {when}',
  'teacher.lesson.firstLesson': 'first lesson',
  'teacher.lesson.notesLabel': 'Lesson notes (only you see these)',
  'teacher.lesson.notesPlaceholder': 'What you worked on, and what to pick up next week.',
  'teacher.lesson.recentAttendance': 'Recent attendance',
  'teacher.lesson.periodSummary':
    '{attended} of {total} attended this period. Attendance feeds the student’s payment summary.',
  'teacher.lesson.messageStudent': 'Message student',
  'teacher.lesson.cancelLesson': 'Cancel this lesson',
  'teacher.lesson.confirmCancelTitle': 'Cancel this lesson?',
  'teacher.lesson.confirmCancelBody':
    'The student is told, the hour goes back on your public page, and the lesson will not count towards their period.',
  'teacher.lesson.confirmCancelAction': 'Yes, cancel it',
  'teacher.lesson.cancelFailed': 'We could not cancel that lesson.',
  'teacher.lesson.cancelledTitle': 'This lesson is cancelled.',
  'teacher.lesson.cancelledOn': 'Cancelled on {when}.',
  'teacher.lesson.cancelledBody': 'There is no attendance to record.',
  'teacher.lesson.notFound': 'We could not find that lesson.',

  'teacher.attendance.lead':
    'Mark who came. It saves the moment you tap — no confirm step, no lost register.',
  'teacher.attendance.heading': 'Attendance',
  'teacher.attendance.present': 'Present',
  'teacher.attendance.absent': 'Absent',
  'teacher.attendance.late': 'Late',
  'teacher.attendance.unmarked': 'Not marked',
  'teacher.attendance.presentInitial': 'P',
  'teacher.attendance.absentInitial': 'A',
  'teacher.attendance.lateInitial': 'L',
  'teacher.attendance.controlLabel': 'Attendance for {name}',
  'teacher.attendance.rangeLabel': 'Which register to show',
  'teacher.attendance.rangeToday': 'Today',
  'teacher.attendance.rangeWeek': 'This week',
  'teacher.attendance.rangePending': 'Needs attention',
  'teacher.attendance.previousDay': 'Previous day',
  'teacher.attendance.nextDay': 'Next day',
  'teacher.attendance.markAllPresent': 'Mark all present',
  'teacher.attendance.lessonCount.one': '1 lesson',
  'teacher.attendance.lessonCount.other': '{count} lessons',
  'teacher.attendance.markedCount.other': '{count} marked',
  'teacher.attendance.toGoCount.other': '{count} to go',
  'teacher.attendance.stillUnmarked': '{date} — still unmarked',
  'teacher.attendance.saved': 'Attendance saved',
  'teacher.attendance.undo': 'Undo',
  'teacher.attendance.saveFailedTitle': 'That did not save.',
  'teacher.attendance.saveFailedBody': 'The register is unchanged. Try tapping again.',
  'teacher.attendance.emptyDayTitle': 'Nothing booked this day.',
  'teacher.attendance.emptyDayBody': 'Page to another day, or switch to This week.',
  'teacher.attendance.allClearTitle': 'Every register is marked.',
  'teacher.attendance.allClearBody': 'Nothing is waiting on you.',

  'teacher.slot.available': 'Available',
  'teacher.slot.availableHint': 'Free — click to book',
  'teacher.slot.past': 'Past',
  'teacher.slot.pastHint': 'Already gone',
  'teacher.slot.unavailable': 'Unavailable',
  'teacher.slot.unavailableHint': 'Outside working hours',
  'teacher.slot.lesson': 'Lesson',
  'teacher.slot.lessonHint': 'Regular lesson',
  'teacher.slot.trial': 'Trial',
  'teacher.slot.trialHint': '30-minute trial lesson',
  'teacher.slot.present': 'Present',
  'teacher.slot.presentHint': 'Student attended',
  'teacher.slot.absent': 'Absent',
  'teacher.slot.absentHint': 'Marked no-show',
  'teacher.slot.late': 'Late',
  'teacher.slot.lateHint': 'Arrived late',
  'teacher.slot.cancelled': 'Cancelled',
  'teacher.slot.cancelledHint': 'Cancelled — not bookable',

  'teacher.schedule.caption': 'Your lessons for the week, by day and time',
  'teacher.schedule.timeColumn': 'Time',
  'teacher.schedule.closed': 'closed',
  'teacher.schedule.lessonCount.one': '1 lesson',
  'teacher.schedule.lessonCount.other': '{count} lessons',
  'teacher.schedule.bookSlot': 'Book {weekday} at {time}',
  'teacher.schedule.viewLabel': 'How to show the week',
  'teacher.schedule.viewWeek': 'Week',
  'teacher.schedule.viewDay': 'Day',
  'teacher.schedule.viewList': 'List',
  'teacher.schedule.emptyWeekTitle': 'Nothing booked this week.',
  'teacher.schedule.emptyWeekBody': 'Your free hours are on your public page, ready to be booked.',
  'teacher.schedule.offGrid': 'Outside {from} – {to}',
  'teacher.schedule.privacyNote':
    'You can see student names here because this is your own schedule. The public teacher page shows these same slots only as “Unavailable”.',

  'teacher.students.title': 'Your students',
  'teacher.students.lead.one':
    'One active student. Attendance and next lesson at a glance — payment details stay with the academy.',
  'teacher.students.lead.other':
    '{count} active students. Attendance and next lesson at a glance — payment details stay with the academy.',
  'teacher.students.leadPlain':
    'Attendance and next lesson at a glance — payment details stay with the academy.',
  'teacher.students.searchLabel': 'Search students',
  'teacher.students.searchPlaceholder': 'Search by name',
  'teacher.students.all': 'All',
  'teacher.students.trials': 'Trials',
  'teacher.students.needsAttention': 'Needs attention',
  'teacher.students.onTrack': 'On track',
  'teacher.students.missed.one': '1 missed',
  'teacher.students.missed.other': '{count} missed',
  'teacher.students.trialToday': 'Trial today',
  'teacher.students.trialBooked': 'Trial booked',
  'teacher.students.trial': 'trial',
  'teacher.students.since': 'Since {when}',
  'teacher.students.noLessonsYet': 'No lessons yet',
  'teacher.students.attendedRatio': '{attended} of {total} attended',
  'teacher.students.emptyTitle': 'No students match that.',
  'teacher.students.emptyBody': 'Clear the filters, or search for a different name.',

  'teacher.dashboard.pendingAlertTitle.one': 'One lesson still needs attendance.',
  'teacher.dashboard.pendingAlertTitle.other': '{count} lessons still need attendance.',
  'teacher.dashboard.pendingAlertBody':
    'Marking them keeps each student’s record and their payment period accurate.',
  'teacher.dashboard.lessonsToday': 'Lessons today',
  'teacher.dashboard.trialsThisWeek': 'Trials this week',
  'teacher.dashboard.needAttendance': 'Need attendance',
  'teacher.dashboard.activeStudents': 'Active students',
  'teacher.dashboard.lessonsTodayLine.one': 'one lesson today',
  'teacher.dashboard.lessonsTodayLine.other': '{count} lessons today',
  'teacher.dashboard.nextLessonLine': 'your next one is {when}',
  'teacher.dashboard.todaysLessons': 'Today’s lessons',
  'teacher.dashboard.openSchedule': 'Open schedule',
  'teacher.dashboard.savesAsYouTap': 'Attendance saves as you tap. Nothing else to do.',
  'teacher.dashboard.openRegister': 'Open full register',
  'teacher.dashboard.noLessonsTodayTitle': 'Nothing booked today.',
  'teacher.dashboard.noLessonsTodayBody':
    'A free day. Your week is on the schedule if you want to look ahead.',
  'teacher.dashboard.attendanceToConfirm': 'Attendance to confirm',
  'teacher.dashboard.upcomingTrials': 'Upcoming trials',
  'teacher.dashboard.noTrials': 'No trials booked yet.',
  'teacher.dashboard.firstLesson': 'First lesson',
  'teacher.dashboard.firstLessonLevel': 'First lesson — {level}',
  'teacher.dashboard.bookedWhen': 'Booked {when}',
  'teacher.dashboard.recentChanges': 'Recent schedule changes',
  'teacher.dashboard.noChanges': 'Nothing has changed this fortnight.',
  'teacher.dashboard.changeNew': 'New',
  'teacher.dashboard.changeCancelledLine': '{name} cancelled {when}',
  'teacher.dashboard.changeNewLine': '{name} booked {when}',
  'teacher.dashboard.questions': 'Questions you can answer',
  'teacher.dashboard.questionsAbout': 'Questions about {subject}',
  'teacher.dashboard.noQuestions': 'No unanswered questions right now.',
  'teacher.dashboard.answerAQuestion': 'Answer a question',

  'teacher.community.title': 'Questions you can answer',
  'teacher.community.lead':
    'Unanswered questions first. Your answers carry a Teacher badge, so students know where the guidance came from.',
  'teacher.community.post': 'Post something',
  'teacher.community.unanswered': 'Unanswered',
  'teacher.community.allQuestions': 'All questions',
  'teacher.community.yourActivity': 'Your activity',
  'teacher.community.answersThisYear': 'Answers this year',
  'teacher.community.markedHelpful': 'Marked helpful',
  'teacher.community.waitingOnYou': 'Waiting on you',
  'teacher.community.answeringWell': 'Answering well',
  'teacher.community.answeringWellBody':
    'Students remember one concrete instruction better than five general ones. Name a tempo, a bar, a number of minutes. Never discuss a named student in public.',
  'teacher.community.emptyTitle': 'Nothing waiting.',
  'teacher.community.emptyBody': 'Every question in this filter has an answer. Try All questions.',

  'teacher.notifications.lead': 'Bookings, cancellations and questions.',

  'teacher.profile.title': 'Profile & working hours',
  'teacher.profile.workingHours': 'Working hours',
  'teacher.profile.drivesAvailability': 'Drives public availability',
  'teacher.profile.workingHoursBody':
    'These hours decide which 30-minute slots appear on your public page. Anything outside them shows as closed, not as taken.',
  'teacher.profile.from': 'From',
  'teacher.profile.to': 'to',
  'teacher.profile.saveHours': 'Save working hours',
  'teacher.profile.endBeforeStart': 'The end of the day has to come after the start.',
  'teacher.profile.hoursFailed': 'We could not save those hours.',
  'teacher.profile.publicProfile': 'Public profile',
  'teacher.profile.publicProfileBody': 'This is what people see before they book a trial with you.',
  'teacher.profile.displayName': 'Display name',
  'teacher.profile.subjects': 'Subjects you teach',
  'teacher.profile.subjectsHelper': 'Hold ⌘ or Ctrl to choose more than one.',
  'teacher.profile.intro': 'Short introduction',
  'teacher.profile.saveProfile': 'Save profile',
  'teacher.profile.profileFailed': 'We could not save that.',
  'teacher.profile.notifications': 'Notifications',
  'teacher.profile.pref.newBookings.title': 'New bookings',
  'teacher.profile.pref.newBookings.body': 'Told the moment a student books you.',
  'teacher.profile.pref.cancellations.title': 'Cancellations',
  'teacher.profile.pref.cancellations.body': 'Including late cancellations.',
  'teacher.profile.pref.attendanceReminders.title': 'Attendance reminders',
  'teacher.profile.pref.attendanceReminders.body': 'A nudge if a register is still unmarked.',
  'teacher.profile.pref.communityReplies.title': 'Community replies',
  'teacher.profile.pref.communityReplies.body': 'When someone responds to your answer.',
  'teacher.profile.account': 'Account',
  'teacher.profile.accountBody':
    'Teacher accounts are created by academy staff. To change your email or role, contact the office.',
  'teacher.profile.changePassword': 'Change password',

  'common.cancel': 'Cancel',
  'common.delete': 'Delete',
  'common.and': 'and',
  'common.breadcrumb': 'You are here',
  'admin.language.az': 'Azərbaycan dili',
  'admin.language.en': 'English',
  'admin.language.ru': 'Русский',
  'admin.nav.dashboard': 'Dashboard',
  'admin.nav.bookings': 'Bookings',
  'admin.nav.schedule': 'Schedule',
  'admin.nav.teachers': 'Teachers',
  'admin.nav.students': 'Students',
  'admin.nav.rooms': 'Practice rooms',
  'admin.nav.subjects': 'Subjects',
  'admin.nav.payments': 'Payments',
  'admin.nav.content': 'Content',
  'admin.nav.inbox': 'Inbox',
  'admin.nav.settings': 'Settings',
  'admin.table.showing': 'Showing {from}–{to} of {total}',
  'admin.table.pageOf': 'Page {page} of {pageCount}',
  'admin.table.previousPage': 'Previous page',
  'admin.table.nextPage': 'Next page',
  'admin.table.open': 'Open',
  'admin.filters.clear': 'Clear filters',
  'admin.error.retry': 'The request did not complete. Nothing was changed.',
  'admin.error.actionFailed': 'That did not go through.',
  'admin.error.dashboard': 'The dashboard could not be loaded.',
  'admin.error.bookings': 'Bookings could not be loaded.',
  'admin.error.booking': 'That booking could not be loaded.',
  'admin.error.schedule': 'The schedule could not be loaded.',
  'admin.error.teachers': 'The teacher roster could not be loaded.',
  'admin.error.teacher': 'That teacher could not be loaded.',
  'admin.error.students': 'The student roster could not be loaded.',
  'admin.error.rooms': 'The practice rooms could not be loaded.',
  'admin.error.subjects': 'The subjects could not be loaded.',
  'admin.error.payments': 'The ledger could not be loaded.',
  'admin.error.content': 'That content could not be loaded.',
  'admin.error.inbox': 'The inbox could not be loaded.',
  'admin.error.settings': 'Settings could not be loaded.',
  'admin.duration.minutes.one': '1 minute',
  'admin.duration.minutes.other': '{count} minutes',
  'admin.duration.hours.one': '1 hour',
  'admin.duration.hours.other': '{count} hours',
  'admin.duration.minutesShort.other': '{count} min',
  'admin.duration.hoursShort.other': '{count} h',
  'admin.state.hold': 'On hold',
  'admin.state.confirmed': 'Confirmed',
  'admin.state.attended': 'Attended',
  'admin.state.noShow': 'No-show',
  'admin.state.cancelled': 'Cancelled',
  'admin.state.confirmedLower': 'confirmed',
  'admin.state.attendedLower': 'attended',
  'admin.state.cancelledLower': 'cancelled',
  'admin.payment.paid': 'Paid',
  'admin.payment.unpaid': 'Unpaid',
  'admin.payment.freeTrial': 'Free trial',
  'admin.payment.refundDue': 'Refund due',
  'admin.payment.refunded': 'Refunded',
  'admin.payment.paidLower': 'paid',
  'admin.stat.lessonsToday': 'Lessons today',
  'admin.stat.trialsThisWeek': 'Trials this week',
  'admin.stat.roomHours': 'Room hours booked',
  'admin.stat.collected': 'Collected this month',
  'admin.stat.roomsPublished': 'Rooms published',
  'admin.stat.hoursBooked': 'Hours booked this week',
  'admin.stat.roomIncome': 'Room income this month',
  'admin.stat.utilisation': 'Average utilisation',
  'admin.stat.outstanding': 'Outstanding',
  'admin.stat.refunded': 'Refunded',
  'admin.stat.averageValue': 'Average lesson value',
  'admin.dashboard.morning': 'Good morning, {name}',
  'admin.dashboard.afternoon': 'Good afternoon, {name}',
  'admin.dashboard.evening': 'Good evening, {name}',
  'admin.dashboard.greetingPlain': 'The academy today',
  'admin.dashboard.datePlain': 'Loading today’s bookings…',
  'admin.dashboard.todayCount': '{lessons} lessons and {rooms} room bookings today',
  'admin.dashboard.holdsCount.one': '1 hold expiring',
  'admin.dashboard.holdsCount.other': '{count} holds expiring',
  'admin.dashboard.holdsBanner.title.one': 'One booking is holding a slot.',
  'admin.dashboard.holdsBanner.title.other': '{count} bookings are holding a slot.',
  'admin.dashboard.holdsBanner.body.one':
    'The oldest lapses in a minute and the slot returns to the public calendar.',
  'admin.dashboard.holdsBanner.body.other':
    'The oldest lapses in {count} minutes and the slot returns to the public calendar.',
  'admin.dashboard.needsAttention': 'Needs attention',
  'admin.dashboard.nothingNeedsAttention':
    'Nothing is waiting. No lapsing holds, no unpaid lessons, no unanswered messages.',
  'admin.dashboard.todayAtTheAcademy': 'Today at the academy',
  'admin.dashboard.openSchedule': 'Open schedule',
  'admin.dashboard.openAllBookings': 'Open all bookings',
  'admin.dashboard.openInbox': 'Open inbox',
  'admin.dashboard.openPayments': 'Open payments',
  'admin.dashboard.dayTotals': '{total} bookings today · {remaining} still to happen',
  'admin.dashboard.empty.title': 'Nothing is booked today.',
  'admin.dashboard.empty.body':
    '{date} has no lessons and no room hire. The academy is open — this is a quiet day, not a fault.',
  'admin.dashboard.empty.action': 'Check again',
  'admin.dashboard.unhandledMessages': 'Unhandled messages',
  'admin.dashboard.inboxClear': 'Every enquiry has been answered.',
  'admin.dashboard.monthSoFar': '{month} so far',
  'admin.dashboard.collectedFrom': 'collected from {lessons} lessons and {rooms} room bookings',
  'admin.dashboard.outstanding': 'Outstanding',
  'admin.dashboard.refunded': 'Refunded',
  'admin.dashboard.held': 'Held, not yet paid',
  'admin.dashboard.roomUtilisation': 'Room utilisation today',
  'admin.dashboard.hoursOf': '{booked} / {open} h',
  'admin.dashboard.noRoomsPublished': 'No rooms are published, so none can be booked.',
  'admin.attention.expiring': 'Expiring',
  'admin.attention.unpaid': 'Unpaid',
  'admin.attention.noHours': 'No hours',
  'admin.attention.new': 'New',
  'admin.attention.more.other': '{count} more',
  'admin.attention.review': 'Review',
  'admin.attention.openPayments': 'Open payments',
  'admin.attention.openSchedule': 'Open schedule',
  'admin.attention.openInbox': 'Open inbox',
  'admin.attention.holds.title.one': 'One booking is holding a slot',
  'admin.attention.holds.title.other': '{count} bookings are holding a slot',
  'admin.attention.holds.detail.one':
    'The oldest hold lapses in a minute · the slot returns to the public calendar',
  'admin.attention.holds.detail.other':
    'The oldest hold lapses in {count} minutes · the slot returns to the public calendar',
  'admin.attention.unpaidPast.title.one': 'One lesson was taught but never paid',
  'admin.attention.unpaidPast.title.other': '{count} lessons were taught but never paid',
  'admin.attention.unpaidPast.detail': '{names}, all past their lesson date',
  'admin.attention.noHours.titleOne': '{name} has no availability next week',
  'admin.attention.noHours.title.other': '{count} teachers have no availability next week',
  'admin.attention.noHours.detail':
    'Their profiles are published, so students see a teacher they cannot book',
  'admin.attention.messages.title.one': 'One contact message is unhandled',
  'admin.attention.messages.title.other': '{count} contact messages are unhandled',
  'admin.attention.messages.detail': 'Oldest arrived {when} from the website',
  'admin.attention.messages.detailPlain': 'From the website’s enquiry form',
  'admin.bookings.scopeLabel': 'What to show',
  'admin.bookings.scope.all': 'All',
  'admin.bookings.scope.lessons': 'Lessons',
  'admin.bookings.scope.rooms': 'Rooms',
  'admin.bookings.export': 'Export CSV',
  'admin.bookings.searchLabel': 'Search bookings',
  'admin.bookings.searchPlaceholder': 'Search name or reference',
  'admin.bookings.statusAll': 'Status · All',
  'admin.bookings.teacherAll': 'Teacher · All',
  'admin.bookings.previousMonth': 'Previous month',
  'admin.bookings.nextMonth': 'Next month',
  'admin.bookings.notRegistered': 'Not yet registered',
  'admin.bookings.lessonTitle': '{subject} · {duration}',
  'admin.bookings.trialTitle': '{subject} trial · {duration}',
  'admin.bookings.roomTitle': '{room} · {duration}',
  'admin.bookings.columns.reference': 'Reference',
  'admin.bookings.columns.student': 'Student',
  'admin.bookings.columns.booking': 'Booking',
  'admin.bookings.columns.when': 'When',
  'admin.bookings.columns.status': 'Status',
  'admin.bookings.columns.payment': 'Payment',
  'admin.bookings.columns.teacher': 'Teacher',
  'admin.bookings.empty.title': 'No bookings match those filters.',
  'admin.bookings.empty.filtered':
    'Nothing in {month} matches. Widen the period or clear the status filter.',
  'admin.bookings.empty.month': 'The academy took no bookings in {month}.',
  'admin.booking.allBookings': 'All bookings',
  'admin.booking.subtitle': '{reference} · booked {ago} from the website · {kind}',
  'admin.booking.kindRegular': 'regular lesson',
  'admin.booking.kindTrial': 'trial lesson',
  'admin.booking.kindRoom': 'room hire',
  'admin.booking.messageStudent': 'Message student',
  'admin.booking.cancel': 'Cancel booking',
  'admin.booking.record': 'Booking record',
  'admin.booking.subject': 'Subject',
  'admin.booking.teacher': 'Teacher',
  'admin.booking.room': 'Room',
  'admin.booking.noRoom': 'Not assigned yet',
  'admin.booking.when': 'When',
  'admin.booking.duration': 'Duration',
  'admin.booking.price': 'Price',
  'admin.booking.slotKey': 'Slot key',
  'admin.booking.slotKeyReleased': 'released — the slot is back on the calendar',
  'admin.booking.requestId': 'Idempotency key',
  'admin.booking.timeNote':
    'Times are stored as timestamptz and shown in the academy wall clock, Asia/Baku. The slot key is what the double-book constraint holds; it becomes NULL the moment this booking is cancelled.',
  'admin.booking.contact': 'Contact details, as given at booking',
  'admin.booking.name': 'Name',
  'admin.booking.email': 'Email',
  'admin.booking.phone': 'Phone',
  'admin.booking.experience': 'Experience',
  'admin.booking.language': 'Language',
  'admin.booking.whatsapp': 'WhatsApp contact',
  'admin.booking.optedIn': 'Opted in',
  'admin.booking.optedOut': 'Not opted in',
  'admin.booking.notes': 'Notes',
  'admin.booking.contactNote':
    'Copied at booking time rather than joined to the account. If the student later changes their phone number, the academy still knows which number to ring about this lesson.',
  'admin.booking.history': 'History',
  'admin.booking.scheduled': 'Scheduled',
  'admin.booking.event.created': 'Booking created from the public website',
  'admin.booking.event.confirmed': 'Confirmed',
  'admin.booking.event.confirmedDetail': 'Hold released, slot key written',
  'admin.booking.event.confirmationEmail': 'Confirmation email sent',
  'admin.booking.event.reminder': 'Reminder due {date}, {time}',
  'admin.booking.event.reminderEmail': 'By email, the day before',
  'admin.booking.event.attended': 'Marked attended',
  'admin.booking.event.noShow': 'Marked a no-show',
  'admin.booking.event.cancelledByAcademy': 'Cancelled by the academy',
  'admin.booking.event.cancelledByStudent': 'Cancelled by the student',
  'admin.booking.event.heldUntil': 'Slot held until {time}',
  'admin.booking.event.requestId': 'request id {id}',
  'admin.booking.event.sentTo': 'To {email}',
  'admin.booking.lifecycle': 'Lifecycle',
  'admin.booking.since': 'since {time} on {date}',
  'admin.booking.notYetHappened':
    'The lesson has not happened yet, so attendance cannot be marked.',
  'admin.booking.markAttended': 'Mark attended',
  'admin.booking.markNoShow': 'Mark no-show',
  'admin.booking.unmark': 'Back to confirmed',
  'admin.booking.payment': 'Payment',
  'admin.booking.minorNote': 'Stored as {minor} qəpik. Nothing monetary is a float.',
  'admin.booking.markPaid': 'Mark as paid',
  'admin.booking.markRefunded': 'Mark refunded',
  'admin.booking.markUnpaid': 'Back to unpaid',
  'admin.booking.student': 'Student',
  'admin.booking.lessonsTaken': 'Lessons taken',
  'admin.booking.studentSince': 'Student since',
  'admin.booking.lastLogin': 'Last login',
  'admin.booking.openStudent': 'Open in the roster',
  'admin.booking.confirmCancel.title': 'Cancel this booking?',
  'admin.booking.confirmCancel.subtitle': '{what} with {who}, {date} at {time}',
  'admin.booking.confirmCancel.body':
    '{name} is emailed straight away and the {time} slot returns to the public calendar. The {amount} is still recorded as unpaid — cancelling never raises a refund by itself, so settle the money on the payment card if anything was taken.',
  'admin.booking.confirmCancel.keep': 'Keep booking',
  'admin.booking.toast.attended': 'Marked attended',
  'admin.booking.toast.noShow': 'Marked a no-show',
  'admin.booking.toast.unmarked': 'Back to confirmed',
  'admin.booking.toast.paid': 'Marked as paid',
  'admin.booking.toast.unpaid': 'Back to unpaid',
  'admin.booking.toast.refunded': 'Marked refunded',
  'admin.booking.toast.cancelled': 'Booking cancelled · the slot is back on the calendar',
  'admin.schedule.viewLabel': 'What to show',
  'admin.schedule.resourceLabel': 'Which resource',
  'admin.schedule.allResources': 'All resources',
  'admin.schedule.addClosure': 'Add closure',
  'admin.schedule.week': 'Week {number}',
  'admin.schedule.practiceRoom': 'Practice room',
  'admin.schedule.teacher': 'Teacher',
  'admin.schedule.firstLesson': 'First lesson',
  'admin.schedule.cancelledAgo': 'cancelled {ago}',
  'admin.schedule.holdExpires': 'hold expires {time}',
  'admin.schedule.noWorkingHours': 'No working hours',
  'admin.schedule.unbookable': 'Published but unbookable',
  'admin.schedule.closedNote': '{days} · academy closed',
  'admin.schedule.closedTitle': 'The academy is closed on {date}.',
  'admin.schedule.closedBody':
    'Nothing can be booked, and no teacher’s hours apply. Closures are set in Settings.',
  'admin.schedule.noResources.title': 'Nothing to show for that filter.',
  'admin.schedule.noResources.body':
    'No teacher or room matches. Clear the resource filter to see the whole academy.',
  'admin.schedule.closureHint':
    'A closure applies to every teacher and every room at once. Leave the second date empty to close one day.',
  'admin.schedule.derivedNote':
    'Availability is derived, never stored: a teacher’s weekly rules plus dated exceptions are turned into slots on request. Blocking an afternoon here writes one exception row rather than rewriting every future Monday.',
  'admin.schedule.legend.lesson': 'Lesson',
  'admin.schedule.legend.trial': 'Trial',
  'admin.schedule.legend.room': 'Practice room',
  'admin.schedule.legend.cancelled': 'Cancelled',
  'admin.schedule.legend.unavailable': 'Unavailable',
  'admin.schedule.toast.closureAdded': 'Closure added · the day is now shut for everyone',
  'admin.teachers.searchLabel': 'Search teachers',
  'admin.teachers.searchPlaceholder': 'Search teachers',
  'admin.teachers.subjectAll': 'Subject · All',
  'admin.teachers.publishedOnly': 'Published only',
  'admin.teachers.invite': 'Invite teacher',
  'admin.teachers.inviteHint':
    'Creates a teaching account with no password and an unpublished profile. They choose a password from the emailed link, and you publish the profile once the hours are set.',
  'admin.teachers.fullName': 'Full name',
  'admin.teachers.email': 'Email',
  'admin.teachers.firstSubject': 'First subject',
  'admin.teachers.noSubjectYet': 'Add one later',
  'admin.teachers.sendInvite': 'Send invitation',
  'admin.teachers.columns.teacher': 'Teacher',
  'admin.teachers.columns.subjects': 'Subjects',
  'admin.teachers.columns.rate': 'Rate',
  'admin.teachers.columns.week': 'This week',
  'admin.teachers.columns.status': 'Status',
  'admin.teachers.noSubjects': 'none yet',
  'admin.teachers.perHour': '{amount} / hour',
  'admin.teachers.trialFree': 'trial free',
  'admin.teachers.trialPrice': 'trial {amount}',
  'admin.teachers.lessonsCount.one': '1 lesson',
  'admin.teachers.lessonsCount.other': '{count} lessons',
  'admin.teachers.noLessons': 'no lessons',
  'admin.teachers.notStarted': 'not started',
  'admin.teachers.hoursAvailable.one': '1 h available',
  'admin.teachers.hoursAvailable.other': '{count} h available',
  'admin.teachers.noHoursSet': 'no hours set',
  'admin.teachers.published': 'Published',
  'admin.teachers.unpublished': 'Unpublished',
  'admin.teachers.featured': 'Featured',
  'admin.teachers.footer':
    'Showing {shown} of {total} teachers · {published} published · {featured} featured on the homepage',
  'admin.teachers.noPublicSignUp':
    'Teachers are created by staff — there is no public sign-up for a teaching account.',
  'admin.teachers.empty.title': 'No teachers match those filters.',
  'admin.teachers.empty.body':
    'Nothing on the roster matches. Clear the filters, or invite somebody new.',
  'admin.teachers.toast.invited':
    '{name} invited · the profile is unpublished until you publish it',
  'admin.teacher.allTeachers': 'All teachers',
  'admin.teacher.yearsTeaching.one': '1 year teaching',
  'admin.teacher.yearsTeaching.other': '{count} years teaching',
  'admin.teacher.viewPublic': 'View public profile',
  'admin.teacher.saveChanges': 'Save changes',
  'admin.teacher.weeklyHours': 'Weekly working hours',
  'admin.teacher.weeklyHoursHint':
    'The pattern that repeats every week. Concrete slots are derived from this on request, so moving a Tuesday is one edit rather than a rewrite of every future Tuesday.',
  'admin.teacher.academyClosed': 'Closed — the academy does not open this day',
  'admin.teacher.addWindow': '+ Add window',
  'admin.teacher.removeWindow': 'Remove this window',
  'admin.teacher.weekTotals': '{hours} hours a week · {days} working days',
  'admin.teacher.copyMonday': 'Copy Monday to every weekday',
  'admin.teacher.exceptions': 'Dated exceptions',
  'admin.teacher.exceptionsHint':
    'Holes in — or additions to — the weekly pattern for one date. Everything else keeps repeating.',
  'admin.teacher.addException': '+ Add exception',
  'admin.teacher.noExceptions': 'Nothing dated. The weekly pattern applies from here on.',
  'admin.teacher.unavailable': 'Unavailable',
  'admin.teacher.extraHours': 'Extra hours',
  'admin.teacher.academyClosure': 'Academy closure',
  'admin.teacher.allDay': 'All day',
  'admin.teacher.blockedFor': '{window} — {reason}',
  'admin.teacher.extraFor': '{window} added for {reason}',
  'admin.teacher.extraPlain': '{window} added',
  'admin.teacher.closureSetInSettings': 'Set in Settings — applies to every teacher and room',
  'admin.teacher.closureLocked':
    'An academy closure. Remove it in Settings, where it applies to everyone.',
  'admin.teacher.removeException': 'Remove this exception',
  'admin.teacher.exceptionDate': 'Which date',
  'admin.teacher.extraHoursToggle': 'These are extra hours, not a block',
  'admin.teacher.allDayToggle': 'The whole day',
  'admin.teacher.from': 'From',
  'admin.teacher.to': 'To',
  'admin.teacher.exceptionReason': 'Reason',
  'admin.teacher.visibility': 'Visibility',
  'admin.teacher.publishedHint':
    'Listed on the public teachers page and bookable. Unpublishing hides the profile but keeps every booking already made.',
  'admin.teacher.featuredHint':
    'Gets the midnight card treatment on the homepage. Featuring an unpublished teacher shows nothing.',
  'admin.teacher.teachesAtHome': 'Teaches at home',
  'admin.teacher.teachesAtHomeHint':
    'Adds a home-visit note to the public profile. It does not change availability.',
  'admin.teacher.rates': 'Rates',
  'admin.teacher.hourlyRate': 'Hourly rate',
  'admin.teacher.trialPrice': 'Trial price',
  'admin.teacher.trialPriceHelper': 'Zero means the trial is free.',
  'admin.teacher.minorNote':
    '{amount} is stored as {minor} qəpik, an integer. Nothing monetary is a float.',
  'admin.teacher.impact': 'What a change does',
  'admin.teacher.impactBody.one':
    '1 lesson is already booked inside these hours. Narrowing a window never cancels a booking — it only stops new ones being made. Cancelling is always a deliberate act on the booking itself.',
  'admin.teacher.impactBody.other':
    '{count} lessons are already booked inside these hours. Narrowing a window never cancels a booking — it only stops new ones being made. Cancelling is always a deliberate act on the booking itself.',
  'admin.teacher.toast.hoursSaved': 'Working hours saved · public availability follows them',
  'admin.teacher.toast.ratesSaved': 'Rates saved',
  'admin.teacher.toast.exceptionAdded': 'Exception added',
  'admin.teacher.subjectRates': 'Rate per instrument',
  'admin.teacher.subjectRatesHint':
    'An instrument follows the hourly rate above unless it is given its own.',
  'admin.teacher.noSubjects': 'Teaches no instruments yet.',
  'admin.teacher.sameAsProfile': 'Same as the hourly rate',
  'admin.teacher.changeSubjectRate': 'Change the {subject} rate',
  'admin.teacher.subjectRateLabel': 'Hourly rate for this instrument',
  'admin.teacher.history': 'Recent price changes',
  'admin.teacher.noHistory': 'No price on this record has been changed yet.',
  'admin.teacher.history.subject': '{subject} rate',
  'admin.teacher.toast.subjectRateSaved':
    '{subject} rate saved · lessons already booked keep their price',
  'admin.teacher.portrait': 'Portrait',
  'admin.teacher.portraitHint':
    'Shown on the public profile and on teacher cards. At least 320 × 380 pixels, framed like the other portraits.',
  'admin.teacher.noPortrait':
    'No portrait yet. The public profile shows artwork until one is uploaded.',
  'admin.teacher.uploadPortrait': 'Upload portrait',
  'admin.teacher.replacePortrait': 'Replace',
  'admin.teacher.removePortrait': 'Remove',
  'admin.teacher.portraitOf': 'Portrait of {name}',
  'admin.teacher.toast.portraitSaved': 'Portrait saved · the public profile shows it now',
  'admin.teacher.toast.portraitRemoved': 'Portrait removed · the profile shows artwork again',
  'admin.photo.types':
    'JPEG, PNG or WebP. Large photos are shrunk in the browser before they are sent.',
  'admin.photo.error.tooLarge':
    'That photo is over {mb} MB even after shrinking. Try a smaller file.',
  'admin.photo.error.type':
    'That file is not a photo this browser can read. Use a JPEG, PNG or WebP.',
  'admin.photo.error.dimensions':
    'That photo is too small for this place. It needs at least {width} × {height} pixels.',
  'admin.price.amount': 'Price, {currency}',
  'admin.price.invalid': 'Enter an amount from 0 to 10,000.',
  'admin.price.useProfile': 'Same as the hourly rate ({amount})',
  'admin.price.review': 'Review change',
  'admin.price.back': 'Back',
  'admin.price.save': 'Save price',
  'admin.price.impactFailed': 'Could not count the bookings this change touches.',
  'admin.price.clearedNote':
    'The override is removed, and this instrument follows the hourly rate again.',
  'admin.price.profileRate': 'hourly rate',
  'admin.price.impact.none': 'Nothing is booked ahead yet. Every new booking is charged {amount}.',
  'admin.price.impact.lessons.one':
    '1 lesson is already booked and keeps the price it was quoted. Only bookings made from now on are charged {amount}.',
  'admin.price.impact.lessons.other':
    '{count} lessons are already booked and keep the price they were quoted. Only bookings made from now on are charged {amount}.',
  'admin.price.impact.hours.one':
    '1 hour is already booked and keeps the price it was quoted. Only bookings made from now on are charged {amount}.',
  'admin.price.impact.hours.other':
    '{count} hours are already booked and keep the price they were quoted. Only bookings made from now on are charged {amount}.',
  'admin.students.searchLabel': 'Search students',
  'admin.students.searchPlaceholder': 'Search students',
  'admin.students.statusAll': 'Status · All',
  'admin.students.add': 'Add student',
  'admin.students.addHint':
    'For somebody who rang up. The account is created with no password; they choose one from the emailed link, and the roster badges them Unverified until they do.',
  'admin.students.create': 'Create account',
  'admin.students.phoneHelper': 'The number the academy messages on WhatsApp.',
  'admin.students.columns.student': 'Student',
  'admin.students.columns.phone': 'Phone',
  'admin.students.columns.lessons': 'Lessons',
  'admin.students.columns.seen': 'Last seen',
  'admin.students.columns.status': 'Status',
  'admin.students.manage': 'Manage',
  'admin.students.manageFor': 'Manage {name}’s account',
  'admin.students.noPhone': 'not given',
  'admin.students.taken.one': '1 taken',
  'admin.students.taken.other': '{count} taken',
  'admin.students.noneYet': 'none yet',
  'admin.students.roomsOnly': 'rooms only',
  'admin.students.upcoming.one': '1 upcoming',
  'admin.students.upcoming.other': '{count} upcoming',
  'admin.students.trialUpcoming': 'trial booked',
  'admin.students.nothingBooked': 'nothing booked',
  'admin.students.createdByStaff': 'created by staff',
  'admin.students.today': 'Today, {time}',
  'admin.students.never': 'Never',
  'admin.students.state.active': 'Active',
  'admin.students.state.trialBooked': 'Trial booked',
  'admin.students.state.dormant': 'Dormant',
  'admin.students.state.unverified': 'Unverified',
  'admin.students.state.deactivated': 'Deactivated',
  'admin.students.empty.title': 'No students match those filters.',
  'admin.students.empty.body': 'Nothing on the roster matches. Clear the filters to see everybody.',
  'admin.students.deactivate': 'Deactivate',
  'admin.students.reactivate': 'Reactivate',
  'admin.students.keepAccount': 'Keep account',
  'admin.students.confirmDeactivate.title': 'Deactivate {name}?',
  'admin.students.confirmDeactivate.body':
    '{name} can no longer sign in, and their booking history stays in the academy’s records. This is a soft delete: nothing is erased, because the accounts come and go while the records have to last. Lessons already booked still happen.',
  'admin.students.confirmReactivate.title': 'Reactivate {name}?',
  'admin.students.confirmReactivate.body':
    '{name} can sign in again and everything they had is where they left it. Nothing was deleted while the account was off.',
  'admin.students.state.pending': 'Waiting',
  'admin.students.state.rejected': 'Turned away',
  'admin.students.state.pendingCount.one': 'Waiting · 1',
  'admin.students.state.pendingCount.other': 'Waiting · {count}',
  'admin.students.queue.title.one': '1 person is waiting to be let in',
  'admin.students.queue.title.other': '{count} people are waiting to be let in',
  'admin.students.queue.body':
    'They signed up on the site and cannot see anything in the portal until somebody here confirms them.',
  'admin.students.queue.review': 'Review them',
  'admin.students.approve.title': 'Is {name} one of ours?',
  'admin.students.approve.signedUp': 'signed up {date}',
  'admin.students.approve.body':
    'Approving opens the portal for them and sends them an email saying so. Nothing else about the account changes.',
  'admin.students.approve.confirm': 'Approve',
  'admin.students.approve.reject': 'Turn away',
  'admin.students.approve.later': 'Decide later',
  'admin.students.approve.rejectTitle': 'They will be told',
  'admin.students.approve.rejectBody':
    'The account stays on record and is signed out, so the same address cannot quietly queue again. Nothing is erased.',
  'admin.students.approve.rejectConfirm': 'Turn away',
  'admin.students.approve.noteLabel': 'What to tell them',
  'admin.students.approve.noteHelper': 'Quoted word for word in the email they receive.',
  'admin.students.toast.approved': '{name} is in · the email is on its way',
  'admin.students.toast.rejected': '{name} was turned away · the record is kept',
  'admin.students.toast.created': '{name} added · the invitation is on its way',
  'admin.students.toast.deactivated': '{name} deactivated · the booking history is kept',
  'admin.students.toast.reactivated': '{name} reactivated',
  'admin.rooms.title': 'Rooms',
  'admin.rooms.hint':
    'Unpublishing a room takes it off the public site. Every booking already made still stands.',
  'admin.rooms.viewPublic': 'View public page',
  'admin.rooms.columns.room': 'Room',
  'admin.rooms.columns.capacity': 'Capacity',
  'admin.rooms.columns.equipment': 'Equipment',
  'admin.rooms.columns.open': 'Open',
  'admin.rooms.columns.rate': 'Rate',
  'admin.rooms.columns.published': 'Published',
  'admin.rooms.people.one': '1 person',
  'admin.rooms.people.other': '{count} people',
  'admin.rooms.perHour': '{amount} / h',
  'admin.rooms.publishedFor': 'Publish {name}',
  'admin.rooms.openFor': 'Open {name}’s public page',
  'admin.rooms.footer': '{total} rooms · {published} published',
  'admin.rooms.hiddenNote': 'Hidden from the public site: {names}',
  'admin.rooms.empty.title': 'No rooms yet.',
  'admin.rooms.empty.body': 'The academy has no practice rooms on record.',
  'admin.rooms.toast.published': '{name} is on the public site',
  'admin.rooms.toast.hidden': '{name} hidden · bookings already made still stand',
  'admin.rooms.changeRateFor': 'Change {name}’s hourly rate',
  'admin.rooms.rateLabel': 'Hourly rate',
  'admin.rooms.toast.rateSaved': '{name} rate saved · bookings already made keep their price',
  'admin.rooms.columns.photos': 'Photos',
  'admin.rooms.photoCount.one': '1 photo',
  'admin.rooms.photoCount.other': '{count} photos',
  'admin.rooms.photosFor': 'Manage {name}’s photos',
  'admin.rooms.photos.hint':
    'The first photo is the cover on the rooms page; the next three fill the gallery. At least 420 × 236 pixels, up to {limit} photos.',
  'admin.rooms.photos.empty': 'No photos yet. The public page shows artwork until one is added.',
  'admin.rooms.photos.add': 'Add photo',
  'admin.rooms.photos.full': 'This room already has {limit} photos. Remove one to add another.',
  'admin.rooms.photos.cover': 'Cover',
  'admin.rooms.photos.makeCover': 'Make photo {index} the cover',
  'admin.rooms.photos.remove': 'Remove photo {index}',
  'admin.rooms.photos.alt': '{name}, photo {index}',
  'admin.rooms.toast.photoAdded': 'Photo added to {name}',
  'admin.rooms.toast.photosSaved': '{name} photos saved',
  'admin.subjects.hint':
    'This order is the order students see on the lessons page. Move a subject with the arrows.',
  'admin.subjects.viewPublic': 'View lessons page',
  'admin.subjects.columns.order': 'Order',
  'admin.subjects.columns.subject': 'Subject',
  'admin.subjects.columns.levels': 'Levels',
  'admin.subjects.columns.teachers': 'Teachers',
  'admin.subjects.columns.lessons': 'Lessons',
  'admin.subjects.columns.published': 'Published',
  'admin.subjects.moveUp': 'Move {name} up',
  'admin.subjects.moveDown': 'Move {name} down',
  'admin.subjects.teacherCount.one': '1 teacher',
  'admin.subjects.teacherCount.other': '{count} teachers',
  'admin.subjects.noTeachers': 'no teachers yet',
  'admin.subjects.publishedTeachers.one': 'only 1 published',
  'admin.subjects.publishedTeachers.other': 'only {count} published',
  'admin.subjects.lessonsBooked.one': '1 lesson booked',
  'admin.subjects.lessonsBooked.other': '{count} lessons booked',
  'admin.subjects.notBookable': 'not bookable',
  'admin.subjects.publishedFor': 'Publish {name}',
  'admin.subjects.openFor': 'Open the {name} page',
  'admin.subjects.footer': '{total} subjects · {published} published',
  'admin.subjects.bookableNote':
    'A subject with no published teacher cannot be booked, whatever its own flag says.',
  'admin.subjects.empty.title': 'No subjects yet.',
  'admin.subjects.empty.body': 'The academy teaches nothing on record.',
  'admin.subjects.toast.published': '{name} is on the lessons page',
  'admin.subjects.toast.hidden': '{name} hidden from the lessons page',
  'admin.subjects.columns.prices': 'Prices',
  'admin.subjects.priceOptions.one': '1 price',
  'admin.subjects.priceOptions.other': '{count} prices',
  'admin.subjects.pricesNotSet': 'Not set',
  'admin.subjects.editPrices': 'Edit prices for {name}',
  'admin.subjects.prices.title': 'Prices · {name}',
  'admin.subjects.prices.hint':
    'Shown on the subject page. Leave a field empty if the option is not offered. A booking still costs the teacher’s lesson price.',
  'admin.subjects.prices.discount': '8 lessons a month, with discount',
  'admin.subjects.prices.discountTooHigh': 'Has to be below the 8-lessons-a-month price.',
  'admin.subjects.prices.threeMonth': '3-month package, price per month',
  'admin.subjects.toast.pricesSaved': 'Prices saved for {name}',
  'admin.payments.statusAll': 'Status · All',
  'admin.payments.columns.date': 'Date',
  'admin.payments.columns.student': 'Student',
  'admin.payments.columns.what': 'What it was for',
  'admin.payments.columns.amount': 'Amount',
  'admin.payments.columns.status': 'Status',
  'admin.payments.columns.receipt': 'Receipt',
  'admin.payments.subjectLesson': '{subject} lesson',
  'admin.payments.trialLesson': 'Trial lesson',
  'admin.payments.roomFor': '{room}, {hours} hours',
  'admin.payments.receipt': 'Receipt',
  'admin.payments.footer': 'Showing {shown} of {total} payments in {month}',
  'admin.payments.minorNote': 'Every amount is stored in qəpik as an integer.',
  'admin.payments.cancelNote':
    'A cancelled booking does not raise a refund by itself — a paid one becomes a refund due, and somebody settles it.',
  'admin.payments.empty.title': 'No payments match those filters.',
  'admin.payments.empty.body':
    'Nothing in {month} has the status “{status}”. Widen the period or clear the status filter.',
  'admin.content.tabsLabel': 'What to edit',
  'admin.content.testimonials': 'Testimonials',
  'admin.content.faq': 'FAQ',
  'admin.content.community': 'Community posts',
  'admin.content.homepage': 'Homepage',
  'admin.content.testimonialsHint':
    'Quotes on the homepage and the subject pages. Unpublishing hides one everywhere at once.',
  'admin.content.addTestimonial': 'Add testimonial',
  'admin.content.editTestimonial': 'Edit testimonial',
  'admin.content.notLinked': 'Not linked',
  'admin.content.rating': '{rating} out of 5',
  'admin.content.ratingLabel': 'Rating',
  'admin.content.outOfFive': '{rating} out of 5',
  'admin.content.quote': 'Quote',
  'admin.content.authorName': 'Author name',
  'admin.content.authorContext': 'Author context',
  'admin.content.linkedTeacher': 'Linked teacher',
  'admin.content.published': 'Published',
  'admin.content.publishedHint': 'Unpublishing hides it everywhere at once.',
  'admin.content.publishedFor': 'Publish the quote from {name}',
  'admin.content.noTestimonials': 'No testimonials yet.',
  'admin.content.noTestimonialsBody':
    'The homepage and the subject pages have nothing to quote. Add the first one.',
  'admin.content.faqHint':
    'The public FAQ page, grouped by category. The booking wizard reads the booking ones.',
  'admin.content.addFaq': 'Add FAQ item',
  'admin.content.editFaq': 'Edit FAQ item',
  'admin.content.question': 'Question',
  'admin.content.answer': 'Answer',
  'admin.content.category': 'Category',
  'admin.content.categoryHelper': 'In use: {list}',
  'admin.content.communityHint':
    'Titles and counts only. Nothing here shows a student’s drafts or a teacher’s private notes — the only action is to take a public thread down.',
  'admin.content.answers.one': '1 answer',
  'admin.content.answers.other': '{count} answers',
  'admin.content.helpful.one': '1 found it useful',
  'admin.content.helpful.other': '{count} found it useful',
  'admin.content.read': 'Read',
  'admin.content.noThreads': 'No community threads yet.',
  'admin.content.noThreadsBody': 'Nobody has asked anything in the community.',
  'admin.content.homepageHint':
    'What the front page is currently made of. Everything except the featured flags is composed from the other screens.',
  'admin.content.viewHomepage': 'View homepage',
  'admin.content.featuredTeachers': 'Featured teachers',
  'admin.content.featuredHint':
    'Featured teachers get the midnight card treatment. Featuring an unpublished teacher shows nothing.',
  'admin.content.featuredFor': 'Feature {name} on the homepage',
  'admin.content.openTeacher': 'Open {name}’s record',
  'admin.content.whatIsLive': 'What is live',
  'admin.content.publishedCount.one': '1 published',
  'admin.content.publishedCount.other': '{count} published',
  'admin.content.toast.saved': 'Saved',
  'admin.content.toast.deleted': 'Deleted',
  'admin.content.toast.threadPublished': 'Thread is public again',
  'admin.content.toast.threadHidden': 'Thread taken down · the answers on it are untouched',
  'admin.inbox.tabsLabel': 'Which messages',
  'admin.inbox.unhandled': 'Unhandled',
  'admin.inbox.handled': 'Handled',
  'admin.inbox.all': 'All',
  'admin.inbox.searchLabel': 'Search messages',
  'admin.inbox.searchPlaceholder': 'Search messages',
  'admin.inbox.handledBadge': 'Handled',
  'admin.inbox.unhandledBadge': 'Unhandled',
  'admin.inbox.received': 'received {date}, {time}',
  'admin.inbox.writtenIn': 'written in {language}',
  'admin.inbox.internalNote': 'Internal note',
  'admin.inbox.internalNoteHelper':
    'Never sent anywhere and never shown to the person who wrote in. It saves when you click away.',
  'admin.inbox.markHandled': 'Mark handled',
  'admin.inbox.reopen': 'Put it back',
  'admin.inbox.replyByEmail': 'Reply by email',
  'admin.inbox.replySubject': 'MusicVille Academy — your enquiry',
  'admin.inbox.createRoomBooking': 'Create a room booking',
  'admin.inbox.empty.title': 'No messages match.',
  'admin.inbox.empty.body': 'Nothing in this tab. Try All, or clear the search.',
  'admin.inbox.empty.unhandled': 'Every enquiry has been answered.',
  'admin.inbox.empty.unhandledBody':
    'Nothing is waiting. New messages from the contact form arrive here.',
  'admin.inbox.toast.handled': '{name}’s message marked handled',
  'admin.inbox.toast.reopened': '{name}’s message is back in Unhandled',
  'admin.inbox.toast.noteSaved': 'Note saved',
  'admin.settings.tabsLabel': 'Which settings',
  'admin.settings.academy': 'Academy',
  'admin.settings.policy': 'Booking policy',
  'admin.settings.closures': 'Academy closures',
  'admin.settings.staff': 'Staff and roles',
  'admin.settings.saveChanges': 'Save changes',
  'admin.settings.academyName': 'Academy name',
  'admin.settings.publicPhone': 'Contact phone (WhatsApp)',
  'admin.settings.address': 'Address',
  'admin.settings.defaultLanguage': 'Default language',
  'admin.settings.contactEmail': 'Contact email',
  'admin.settings.timeZone': 'Time zone',
  'admin.settings.opensAt': 'Opens at',
  'admin.settings.closesAt': 'Closes at',
  'admin.settings.hoursHint': 'Closing days are set under Academy closures below.',
  'admin.settings.legalPages': 'Legal pages',
  'admin.settings.legalDraft': 'Show the “draft” notice',
  'admin.settings.legalDraftHelper':
    'Turn this off once a lawyer has read the booking policy and the privacy notice. Visitors see the change straight away.',
  'admin.settings.timeZoneHelper':
    'Fixed. The academy runs on one wall clock and the code applies it at the edges only.',
  'admin.settings.policyHint':
    'These numbers decide what a student can and cannot do without ringing the academy.',
  'admin.settings.slotHold': 'Slot hold',
  'admin.settings.trialLength': 'Trial length',
  'admin.settings.cancellationWindow': 'Cancellation window',
  'admin.settings.minimumNotice': 'Minimum notice',
  'admin.settings.bookingHorizon': 'Booking horizon',
  'admin.settings.trialPrice': 'Trial price',
  'admin.settings.free': 'Free',
  'admin.settings.noWindow': 'Any time before the lesson',
  'admin.settings.noNotice': 'No minimum',
  'admin.settings.hoursBefore.one': '1 hour before the lesson',
  'admin.settings.hoursBefore.other': '{count} hours before the lesson',
  'admin.settings.daysAhead.one': '1 day ahead',
  'admin.settings.daysAhead.other': '{count} days ahead',
  'admin.settings.closuresHint':
    'A closure applies to every teacher and every room at once. One weekly row closes that day every week.',
  'admin.settings.noClosures': 'The academy never closes. Add a weekly closing day or a date.',
  'admin.settings.addClosure': 'Add closure',
  'admin.settings.removeClosure': 'Reopen this day',
  'admin.settings.closureKind': 'What kind',
  'admin.settings.oneDate': 'One date',
  'admin.settings.dateRange': 'Date range',
  'admin.settings.recurring': 'Recurring',
  'admin.settings.closingDay': 'Closing day',
  'admin.settings.closureFrom': 'From',
  'admin.settings.closureTo': 'To',
  'admin.settings.closureToHelper': 'Leave empty to close one day.',
  'admin.settings.closureReason': 'Reason',
  'admin.settings.closureReasonPlaceholder': 'Christmas Day, Novruz, a staff day…',
  'admin.settings.noReason': 'No reason recorded',
  'admin.settings.everyWeekday': 'Every {weekday}',
  'admin.settings.you': 'You',
  'admin.settings.pending': 'Invited',
  'admin.settings.role': 'Role',
  'admin.settings.administrator': 'Administrator',
  'admin.settings.inviteStaff': 'Invite staff',
  'admin.settings.sendInvite': 'Send invitation',
  'admin.settings.staffNote':
    'There is no public sign-up for a staff account. Inviting somebody creates a user with no password set; the invitation link is what lets them choose one.',
  'admin.settings.whoSeesWhat': 'Who sees what',
  'admin.settings.whoSeesWhatHint': 'The privacy line the whole product is built around.',
  'admin.settings.role.guest': 'Guest',
  'admin.settings.role.guestBody':
    'Availability as free or busy. No names, no schedules, nothing financial.',
  'admin.settings.role.student': 'Student',
  'admin.settings.role.studentBody': 'Only their own lessons, bookings and payments.',
  'admin.settings.role.teacher': 'Teacher',
  'admin.settings.role.teacherBody':
    'Their own students, inside their own schedule. Nothing financial.',
  'admin.settings.role.administrator': 'Administrator',
  'admin.settings.role.administratorBody':
    'Everything, including every payment and every contact detail.',
  'admin.settings.oneWallClock': 'One wall clock',
  'admin.settings.oneWallClockBody':
    'The academy runs on Asia/Baku and that is applied at the edges only — every instant in the database is timestamptz, and working hours are integers counted from local midnight. The offset is asked of the system rather than hard-coded, so the day Azerbaijan reintroduces daylight saving nothing here quietly breaks.',
  'admin.settings.toast.saved': 'Settings saved',
  'admin.settings.toast.closureAdded': 'Closure added',
  'admin.settings.toast.closureRemoved': 'Closure removed · the day is open again',
  'admin.settings.toast.invited': 'Invitation sent',
  'admin.settings.toast.roleChanged': 'Role changed',
  'admin.signIn.title': 'Staff sign-in',
  'admin.signIn.subtitle': 'Administration and teaching accounts.',
  'admin.signIn.submit': 'Sign in',
  'admin.signIn.failed': 'That email and password do not match.',
  'admin.signIn.noSignUp':
    'Staff accounts are created by an administrator. There is no public sign-up behind this door — an invitation is the only way in.',
  'admin.signIn.studentLink': 'Student? Sign in on the main site',
  'validation.phoneRequired': 'Enter a phone number we can reach you on',
  'validation.phoneInvalid': 'Enter a valid phone number',
  'validation.passwordMin': 'Use at least 10 characters',
  'validation.tooLong': 'That is longer than we can store',
  'validation.date': 'Expected a YYYY-MM-DD date',
  'validation.month': 'Expected a YYYY-MM month',
  'validation.nameRequired': 'Tell us your name',
  'validation.termsRequired': 'Please accept the booking policy to continue',
  'validation.passwordRequired': 'Enter your password',
  'validation.passwordsMatch': 'Both passwords need to match',
  'validation.code': 'Enter the 6-digit code',
  'validation.tokenOrCode': 'Provide either a verification link token or the emailed code',
  'validation.email': 'Enter a valid email address',
  'validation.notesTooLong': 'Keep the note under 600 characters',
  'validation.chooseTime': 'Choose a time',
  'validation.chooseSubject': 'Choose a subject',
  'validation.chooseTeacher': 'Choose a teacher',
  'validation.chooseHour': 'Choose at least one hour',
  'validation.messageShort': 'A sentence or two is enough',
  'validation.questionShort': 'Ask it the way you would say it out loud',
  'validation.questionLong': 'Keep the question to a single sentence',
  'validation.answerShort': 'Say a little more — one line rarely answers it',
  'validation.currentPasswordRequired': 'Enter your current password',
  'validation.weekdayOnce': 'Each weekday may appear once',
  'validation.dayNeedsHours': 'Set both a start and an end for a day you work',
  'validation.dayEndAfterStart': 'The end of the day has to come after the start',
  'validation.windowEndAfterStart': 'The end of a window has to come after its start',
  'validation.windowsOverlap': 'Windows on one day cannot overlap',
  'validation.extraHoursNeedBoth': 'Extra hours need a start and an end',
  'validation.bothOrNeither': 'Give both a start and an end, or neither for the whole day',
  'validation.endAfterStart': 'The end has to come after the start',
  'validation.photoOnce': 'Each photo can appear once',
  'validation.discountBelowMonthly': 'The discount has to be below the monthly price',
  'validation.required': 'Required',
  'validation.closeAfterOpen': 'Closing time must be after opening time',
  'validation.pickWeekday': 'Pick the day of the week',
  'validation.pickDate': 'Pick a date',
  'validation.pickRangeEnd': 'Pick the last day of the range',
  'validation.rangeEndAfterStart': 'The range has to end after it starts',
};
